import { researchNotes202609 } from "./researchNotes202609";
import { chinaRobotics20260924 } from "./chinaRobotics20260924";
import { chinaPhysicalAIData20260924 } from "./chinaPhysicalAIData20260924";
import { chinaTrends20260924 } from "./chinaTrends20260924";
import { perceptronMk15Article20260924 } from "./perceptronMk15Article20260924";
import { currentRoboticsNews20260914 } from "./currentRoboticsNews20260914";
import { applyEditorialMedia } from "./editorialMedia";
import { humanoidOperationsArticles2026 } from "./humanoidOperationsArticles2026";
import { physicalAIClusterLibrary2026 } from "./physicalAIClusterLibrary2026";
import { featureArticles2026 } from "./featureArticles2026";
import { physicalAIArticles2026 } from "./physicalAIArticles2026";
import { editorialLibrary2026 } from "./editorialLibrary2026";
import { seoArticles2026 } from "./seoArticles2026";
import { seoArticlesBatch2_2026 } from "./seoArticlesBatch2_2026";
import { searchConsoleArticles2026 } from "./searchConsoleArticles2026";
import { trendingPhysicalAIArticles2026 } from "./trendingPhysicalAIArticles2026";
import { robotResearchFrontiers2026 } from "./robotResearchFrontiers2026";

const legacyNewsArticleEntries = [
{
  "id": "agibot-a3-wita-omni-macao-film-concert",
  "slug": "agibot-a3-wita-omni-macao-film-concert",
  "path": "/robotics-news/agibot-a3-wita-omni-macao-film-concert",
  "title": "AGIBOT A3 joins the conversation on stage in Macao",
  "seoTitle": "AGIBOT A3 in Macao and how WITA-Omni connects speech and motion",
  "seoDescription": "AGIBOT A3 joined celebrities in Macao with WITA-Omni. A closer look at speech and motion timing, the model architecture and its benchmark limits.",
  "subtitle": "The concert appearance puts attention on how a humanoid follows a conversation while moving and speaking.",
  "description": "AGIBOT A3 joined celebrities at the Greater Bay Area Film Concert in Macao. AGIBOT describes an exchange in which the robot followed the conversation in real time and coordinated its speech with movements and expressions through WITA-Omni.",
  "category": "Human-robot interaction",
  "categorySlugs": [
    "humanoid-robots",
    "entertainment-robots",
    "ai-powered-robotic-platforms"
  ],
  "datePublished": "2026-09-20",
  "dateModified": "2026-09-20",
  "readingTime": "5 min read",
  "authorName": "TechniaHQRobot",
  "schemaType": "NewsArticle",
  "showTableOfContents": true,
  "primaryKeyword": "AGIBOT A3 WITA-Omni",
  "keywords": [
    "AGIBOT A3",
    "WITA-Omni",
    "human-robot interaction",
    "Greater Bay Area Film Concert",
    "Macao",
    "humanoid robots",
    "embodied AI"
  ],
  "tags": [
    "AGIBOT",
    "WITA-Omni",
    "Humanoid robots",
    "Embodied AI"
  ],
  "image": "/images/humanoid-catalog/agibot/agibot-a3.png",
  "imageAlt": "AGIBOT A3 humanoid robot shown from the front",
  "imageWidth": 320,
  "imageHeight": 426,
  "imageCaption": "AGIBOT A3 product image. The concert sequence appears in the embedded post below.",
  "imageCredit": {
    "name": "AGIBOT",
    "url": "https://www.agibot.com/products/A3"
  },
  "tweetId": "2101664230361465046",
  "tweetUrl": "https://x.com/techniahqrobot/status/2101664230361465046",
  "sponsoredDisclosure": "Disclosure. TechniaHQRobot has a commercial relationship with AGIBOT.",
  "introduction": [
    "A robot sharing a stage with people has to fit into their timing. Someone finishes a sentence. Another person starts speaking. A gesture can arrive too late or continue after the conversation has moved on.",
    "That timing is what catches my attention in AGIBOT's description of A3 in Macao. The robot was presented as following the exchange while coordinating its voice and body. The technical question is how those outputs stay connected as new speech arrives."
  ],
  "sections": [
    {
      "heading": "A conversation inside a stage performance",
      "paragraphs": [
        "AGIBOT says A3 interacted with celebrities at the Greater Bay Area Film Concert in Macao. Its event post identifies WITA-Omni as the system behind the robot's perception and responses. The account describes coordinated speech, movement and expression during the exchange.",
        "The embedded TechniaHQRobot post lets readers return to the sequence while reading this article. Watch when the robot begins its response and how its movements relate to that response. Those are useful details to examine before drawing conclusions about the whole system.",
        "The event description establishes the setting and the company's account of the interaction. It does not establish how much of the exchange was rehearsed or what supervision was available. Calling it a fully unscripted autonomous conversation would require further evidence."
      ],
      "sources": [
        {
          "label": "TechniaHQRobot concert post",
          "url": "https://x.com/techniahqrobot/status/2101664230361465046"
        }
      ]
    },
    {
      "heading": "How WITA-Omni connects speech and movement",
      "paragraphs": [
        "AGIBOT describes WITA-Omni through three components named Thinker, Talker and Actor. They share a model state and timeline. The design keeps incoming observations connected with the robot's outgoing responses.",
        "Thinker processes text, images, audio and video with sound. Talker produces speech from its internal state. Actor adds movement and facial-expression outputs alongside that speech. The model is designed to keep receiving observations while preparing and producing a response."
      ],
      "table": {
        "id": "wita-omni-components",
        "title": "The three components",
        "columns": [
          "Component",
          "Role"
        ],
        "rows": [
          [
            "Thinker",
            "Combines incoming information and makes interaction decisions."
          ],
          [
            "Talker",
            "Generates the spoken response."
          ],
          [
            "Actor",
            "Produces movement and expression outputs alongside speech."
          ]
        ]
      },
      "subsections": [
        {
          "heading": "Learning when to respond",
          "paragraphs": [
            "AGIBOT describes supervised fine-tuning, on-policy distillation and reinforcement learning in the training process. The stated goals include response accuracy, timing, choosing the person to address and deciding whether to wait.",
            "These are the published design goals. The concert post does not provide a separate measurement of each capability."
          ]
        }
      ],
      "sources": [
        {
          "label": "AGIBOT technical description of WITA-Omni",
          "url": "https://www.agibot.com/article/231/detail/86.html"
        }
      ]
    },
    {
      "heading": "Why a well-timed answer needs several measurements",
      "paragraphs": [
        "A useful way to assess this interaction is to follow three moments. First comes the end of the person's turn. Next comes the robot's first audible response. Then comes the movement associated with that response. A recording with synchronized timestamps would let an evaluator measure the gaps.",
        "A short speech delay alone would leave other questions unanswered. The robot could respond quickly to the wrong person. It could start speaking at the right moment while its arms continue an earlier movement. It could also mistake a pause for the end of a sentence.",
        "These are examples of failure cases to test. They are not failures established for A3 in this concert. They explain why one smooth exchange cannot supply a success rate for conversations with several people."
      ]
    },
    {
      "heading": "What the 85.21 percent Daily-Omni result measures",
      "paragraphs": [
        "The Daily-Omni leaderboard lists WITA-Omni Preview at 85.21 percent average accuracy when checked for this article on 20 September 2026. The benchmark authors describe 684 videos and 1,197 multiple-choice questions across six task families.",
        "The questions test relationships between sound, visible events and their timing. The leaderboard includes audio-visual alignment, comparison, context understanding, event sequence, inference and reasoning.",
        "That result gives readers a concrete evaluation of audio-visual reasoning. It does not measure the delay between a person's question and a robot's spoken answer. Nor does it score the timing of arm movement, physical balance or operator involvement at the concert.",
        "The leaderboard names WITA-Omni Preview. The event description uses WITA-Omni without identifying a model version. The benchmark score should therefore remain attached to the named Preview model and its test conditions."
      ],
      "sources": [
        {
          "label": "Daily-Omni leaderboard",
          "url": "https://lliar-liar.github.io/Daily-Omni/#leaderboard"
        },
        {
          "label": "Daily-Omni research paper",
          "url": "https://arxiv.org/abs/2505.17862"
        }
      ]
    },
    {
      "heading": "The A3 hardware behind the appearance",
      "paragraphs": [
        "AGIBOT lists A3 at 173 cm tall and 55 kg. Its product page describes microphones arranged for audio pickup around the robot, shoulder touch sensing and voice interaction. These features give context to a machine intended to share space with people.",
        "The company also advertises up to 10 hours of endurance and a 10-second battery swap. Those are product claims. The concert material does not establish the runtime of the robot used in Macao or the workload behind the maximum endurance figure.",
        "For this appearance, the relevant hardware question is how well the sensors and body support an ongoing exchange. A microphone specification cannot tell us how often the robot selects the correct speaker. A video of coordinated gestures cannot reveal the full control system."
      ],
      "sources": [
        {
          "label": "AGIBOT A3 product specifications",
          "url": "https://www.agibot.com/products/A3"
        }
      ]
    },
    {
      "heading": "What a longer interaction test could show",
      "paragraphs": [
        "The cited material does not provide the concert's measured response latency, a count of completed conversational turns or a record of operator interventions. A longer recording with those measurements would make the result easier to assess.",
        "A useful follow-up would keep the same robot in a conversation while speakers take turns, interrupt and resume. Report how often it addresses the intended person. Measure the delay to speech and the timing difference between speech and its associated gesture. Include the attempts that need assistance.",
        "My interest is in whether that coordination holds through a whole exchange. The Macao appearance gives readers a concrete scene to examine. Repeated interactions with published timing data would show how consistently A3 can do it."
      ]
    }
  ],
  "methodology": [
    "The event account comes from AGIBOT's post text supplied for this article and the linked TechniaHQRobot post. Architecture and hardware details come from AGIBOT. The benchmark score was checked against the Daily-Omni leaderboard. The proposed interaction tests are TechniaHQRobot analysis."
  ],
  "sourceLinks": [
    {
      "label": "TechniaHQRobot post from the Macao concert",
      "url": "https://x.com/techniahqrobot/status/2101664230361465046",
      "organization": "TechniaHQRobot"
    },
    {
      "label": "WITA-Omni architecture and training description",
      "url": "https://www.agibot.com/article/231/detail/86.html",
      "organization": "AGIBOT"
    },
    {
      "label": "A3 product specifications",
      "url": "https://www.agibot.com/products/A3",
      "organization": "AGIBOT"
    },
    {
      "label": "Daily-Omni benchmark leaderboard",
      "url": "https://lliar-liar.github.io/Daily-Omni/#leaderboard",
      "organization": "Daily-Omni authors"
    },
    {
      "label": "Daily-Omni research paper",
      "url": "https://arxiv.org/abs/2505.17862",
      "organization": "Daily-Omni authors"
    }
  ],
  "relatedLinks": [
    {
      "label": "Humanoid robot autonomy levels",
      "path": "/humanoid-robot-autonomy-levels"
    },
    {
      "label": "Humanoid controller architecture",
      "path": "/humanoid-robot-controller-architecture"
    },
    {
      "label": "Physical AI",
      "path": "/physical-ai"
    }
  ]
},
{
  "id": "agibot-world-2026-theme-3-robot-reinforcement-learning",
  "title": "AGIBOT WORLD 2026 Theme 3 opens 11,430 real-world robot trajectories for reinforcement learning",
  "seoTitle": "AGIBOT WORLD 2026 Theme 3 Robot RL Dataset | TechniaHQRobot",
  "slug": "agibot-world-2026-theme-3-robot-reinforcement-learning",
  "path": "/robotics-news/agibot-world-2026-theme-3-robot-reinforcement-learning",
  "category": "Embodied AI datasets",
  "datePublished": "2026-09-04",
  "dateModified": "2026-09-26",
  "readingTime": "6 min read",
  "description": "AGIBOT WORLD 2026 Theme 3 releases 11,430 real-world robot trajectories across 14 tasks, including successful and failed policy rollouts, human corrections and fine-grained execution annotations for reinforcement learning.",
  "primaryKeyword": "AGIBOT WORLD 2026 Theme 3",
  "keywords": [
    "AGIBOT WORLD 2026 Theme 3",
    "AGIBOT WORLD dataset",
    "robot reinforcement learning",
    "real-world robot data",
    "embodied AI dataset",
    "Physical AI",
    "robot policy rollouts",
    "human-in-the-loop robot learning",
    "robot failure data",
    "robot manipulation dataset"
  ],
  "tags": [
    "AGIBOT",
    "Embodied AI",
    "Reinforcement Learning",
    "Robot Data",
    "Physical AI"
  ],
  "sponsoredDisclosure": "Partner content. AGIBOT supported this coverage. TechniaHQRobot independently wrote and verified the article against AGIBOT's official release and linked project resources.",
  "image": "https://www.agibot.com/file/ueditor/php/upload/image/20260903/1788405925925990.gif",
  "imageAlt": "AGIBOT WORLD 2026 Theme 3 expert demonstration trajectory for real-world robot reinforcement learning",
  "trendReason": "The useful part of this release is the failure data. Theme 3 preserves successful rollouts, failed rollouts, disturbances and human corrections instead of reducing robot learning to clean expert demonstrations.",
  "introduction": [
    "Robot learning datasets often show what a successful task should look like. Real robots spend plenty of time outside that clean path. They miss grasps, drift from the intended sequence, encounter disturbances and sometimes need a human to take over.",
    "AGIBOT WORLD 2026 Theme 3 is built around those execution states. AGIBOT's first open-source batch contains 11,430 real-world trajectories across 14 industrial and household tasks, combining expert demonstrations, autonomous policy rollouts and human-in-the-loop corrections."
  ],
  "featuredSnippet": "AGIBOT WORLD 2026 Theme 3 is an open-source real-world reinforcement-learning dataset with 11,430 robot trajectories across 14 tasks. The release includes 1,024 successful policy rollouts, 1,369 failed rollouts, 98,159 annotated subtask intervals, 26,493 disturbance segments, 5,795 error-state segments and 10,684 human-intervention segments.",
  "keyFacts": [
    "11,430 real-world trajectories across 14 industrial and household tasks.",
    "1,024 successful policy rollouts and 1,369 failed policy rollouts.",
    "98,159 annotated subtask intervals with completion-status labels.",
    "26,493 disturbance segments and 5,795 error-state segments.",
    "10,684 human-intervention segments that preserve where takeover begins and how correction proceeds.",
    "The dataset is open source through the AGIBOT WORLD project and Hugging Face."
  ],
  "sections": [
    {
      "heading": "Why failed robot trajectories are valuable training data",
      "paragraphs": [
        "A successful trajectory shows a policy one route through a task. A failed trajectory reveals where that policy stops being reliable. That distinction matters for physical systems because a robot can reach the same failure state through bad perception, poor contact, an incorrect motion sequence or an unexpected change in the environment.",
        "Theme 3 keeps those unsuccessful executions in the dataset. Researchers can inspect where a rollout diverged, how far the task progressed and what happened before a human intervention. That makes the data useful for failure recognition, recovery policies, reward modeling and value estimation rather than imitation alone."
      ]
    },
    {
      "heading": "The dataset combines demonstration, rollout and correction",
      "paragraphs": [
        "AGIBOT divides the release into three complementary trajectory types. Expert demonstrations capture reference executions from human operators. Policy rollouts record autonomous model execution and preserve both successes and failures. Human-in-the-loop trajectories retain the robot's behavior before takeover, the intervention point and the corrective actions that follow.",
        "That structure creates a continuous record from how a task should be performed to how a learned policy actually behaves on hardware. It also preserves the recovery path after something goes wrong, which is often missing from datasets built only from successful demonstrations."
      ]
    },
    {
      "heading": "14 tasks cover precision, contact and longer execution chains",
      "paragraphs": [
        "The first batch spans 14 industrial and household tasks. AGIBOT names examples including Ethernet-cable insertion and unlocking a door with a key. These are useful reinforcement-learning cases because they require more than reaching a visual target. The robot has to manage contact, sequence actions and respond to execution feedback.",
        "The release is therefore relevant to manipulation research where small errors accumulate. A cable can be aligned but not fully inserted. A key can reach the lock but fail to rotate correctly. Those partial states are important because they sit between a clean success label and a total failure label."
      ]
    },
    {
      "heading": "Fine-grained annotations turn execution into learning signals",
      "paragraphs": [
        "AGIBOT reports 98,159 annotated subtask intervals, 26,493 disturbance segments, 5,795 error-state segments and 10,684 human-intervention segments in the first batch. The annotations cover task progress, completion, errors, disturbances and interventions.",
        "For researchers, that opens several directions. A model can be trained to estimate task progress, detect success, recognize errors, warn about risk or decide when human assistance is needed. The intervention segments can also support learning from corrective behavior rather than restarting every failed attempt from scratch."
      ]
    },
    {
      "heading": "What this release changes for embodied AI research",
      "paragraphs": [
        "Real-robot reinforcement learning is expensive because every experiment consumes hardware time and exposes the robot to wear, collisions and recovery work. Shared trajectories cannot replace new physical experiments, but they can reduce duplicated collection and give research teams common data for comparing methods.",
        "The broader shift is from learning only what successful behavior looks like toward modeling what happens during execution. For humanoids and other general-purpose robots, reliable work will depend on recognizing when an action is going wrong and recovering before a small error ends the full task. Theme 3 gives researchers data specifically around that boundary."
      ]
    }
  ],
  "limitations": [
    "This is a dataset release, not evidence that a general-purpose robot can autonomously complete arbitrary industrial or household work.",
    "The published counts describe the released trajectories and annotation segments; they do not by themselves establish policy generalization, deployment reliability or production-level task success.",
    "Researchers still need to evaluate how policies trained or fine-tuned with the dataset transfer to different robots, objects, layouts and operating conditions.",
    "AGIBOT published an earlier Theme 3 page with 9,638 trajectories and more than 164 hours of data. This article follows the later September 1 release, which reports 11,430 trajectories; researchers should record the source version when comparing counts."
  ],
  "conclusion": [
    "AGIBOT WORLD 2026 Theme 3 puts failures, disturbances and corrections beside successful robot behavior. That makes the release useful for a part of embodied AI that is easy to overlook: teaching physical systems to identify the edge of their own competence and recover when execution departs from the intended plan.",
    "AGIBOT says it plans to continue expanding AGIBOT WORLD 2026 with additional datasets, benchmarks and research resources."
  ],
  "faqs": [
    {
      "question": "What is AGIBOT WORLD 2026 Theme 3?",
      "answer": "It is an open-source AGIBOT dataset focused on real-world robot reinforcement learning. It combines expert demonstrations, autonomous policy rollouts and human-in-the-loop corrections with detailed execution annotations."
    },
    {
      "question": "How many trajectories are included?",
      "answer": "The first open-source batch contains 11,430 real-world trajectories across 14 industrial and household tasks."
    },
    {
      "question": "Does the dataset include robot failures?",
      "answer": "Yes. AGIBOT reports 1,369 failed policy rollouts, plus 5,795 error-state segments, 26,493 disturbance segments and 10,684 human-intervention segments."
    },
    {
      "question": "Where is the AGIBOT WORLD dataset available?",
      "answer": "AGIBOT provides the project through agibot-world.com and publishes the open-source dataset on Hugging Face under the agibot-world organization."
    }
  ],
  "sourceLinks": [
    {
      "label": "AGIBOT official release: AGIBOT WORLD 2026 Theme 3",
      "organization": "AGIBOT",
      "date": "September 1, 2026",
      "url": "https://www.agibot.com/article/231/detail/95.html"
    },
    {
      "label": "AGIBOT WORLD project page",
      "organization": "AGIBOT WORLD",
      "url": "https://agibot-world.com/"
    },
    {
      "label": "AgiBotWorld2026 dataset on Hugging Face",
      "organization": "AGIBOT WORLD",
      "url": "https://huggingface.co/datasets/agibot-world/AgiBotWorld2026"
    },
    {
      "label": "AGIBOT official X post",
      "organization": "AGIBOT",
      "url": "https://x.com/AGIBOTofficial/status/2095402214324072731"
    },
    {
      "label": "AGIBOT official LinkedIn post",
      "organization": "AGIBOT",
      "url": "https://www.linkedin.com/posts/agibotofficial_agibot-embodiedai-reinforcementlearning-activity-7501165007859023872-isJg"
    }
  ],
  "tweetUrl": "https://x.com/AGIBOTofficial/status/2095402214324072731",
  "relatedLinks": [
    {
      "label": "Chinese humanoid robots",
      "path": "/humanoid-robots/china"
    },
    {
      "label": "Humanoid robot training data pipeline",
      "path": "/robotics/humanoid-robot-training-data-pipeline"
    },
    {
      "label": "Physical AI intelligence",
      "path": "/physical-ai"
    }
  ],
  "relatedSearches": [
    "AGIBOT WORLD 2026 dataset",
    "AgiBotWorld2026 Hugging Face",
    "robot reinforcement learning dataset",
    "embodied AI failure trajectories",
    "human in the loop robot learning"
  ],
  "showTableOfContents": true,
  "informationVerifiedAsOf": "September 26, 2026"
},
{
  "id": "20-humanoid-robots-you-can-actually-buy-2026",
  "title": "20 Humanoid Robots You Can Actually Buy in 2026",
  "seoTitle": "20 Humanoid Robots You Can Actually Buy in 2026 TechniaHQRobot",
  "slug": "20-humanoid-robots-you-can-actually-buy-2026",
  "path": "/robotics-news/20-humanoid-robots-you-can-actually-buy-2026",
  "category": "Humanoid robot buyers guide",
  "datePublished": "2026-07-11",
  "dateModified": "2026-10-11",
  "readingTime": "18 min read",
  "description": "A source-checked 2026 buyers guide to 20 humanoid robots offered through official stores, deposits or manufacturer quotations, with current purchase status, hardware and practical limits.",
  "keywords": [
    "humanoid robots for sale 2026",
    "buy humanoid robot",
    "humanoid robot price",
    "commercial humanoid robots",
    "research humanoid robot",
    "Unitree R1",
    "Unitree G1",
    "Unitree H2",
    "Unitree H1",
    "Booster K1",
    "Booster T1",
    "Booster T2",
    "1X NEO",
    "Reachy Mini",
    "Reachy 2",
    "Ameca",
    "NAO6",
    "PAL Robotics TALOS",
    "ROBOTIS OP3",
    "THORMANG3",
    "Physical AI",
    "embodied AI"
  ],
  "trendReason": "Buying a humanoid in 2026 still means buying hardware, documentation and a development path, not a finished general-purpose worker. This guide includes only robots with an official checkout, a live deposit program or a manufacturer sales channel that offers the platform commercially.",
  "authorName": "TechniaHQRobot",
  "authorHandle": "@techniahqrobot",
  "sections": [
    {
      "heading": "The humanoid market finally has products, but not one kind of product",
      "paragraphs": [
        "For most of the last decade, asking where to buy a humanoid robot produced a list of research prototypes, conference demonstrations and company videos. In 2026, the answer is more concrete. Several manufacturers operate public online stores. Others accept deposits for scheduled delivery. Established robotics suppliers continue to sell research platforms through quotations and institutional purchase orders.",
        "That does not mean twenty robotic workers are ready to replace a person in a home or factory. The machines below span four very different categories full-size biped development platforms, compact walking robots, wheeled or stationary humanoids built for manipulation and social robots designed around faces, speech and human interaction. Their commercial availability is real. Their autonomy, payload, durability and support requirements vary sharply.",
        "Prices and order status in this guide were checked again on September 25, 2026. Public prices generally exclude shipping, local tax, customs duties, optional hands, additional batteries, service contracts and developer configurations. Quote-only systems can cost more once training, integration and support are included."
      ]
    },
    {
      "heading": "What “you can actually buy” means in this guide",
      "paragraphs": [
        "A robot qualifies here when its manufacturer provides one of three active purchase paths direct checkout with a displayed price, a paid reservation tied to a delivery program or a request-a-quote channel for the named production platform. A contact form alone is not enough when the machine is still described as a prototype. This is why high-profile systems such as Tesla Optimus, Figure, Apptronik Apollo and Boston Dynamics Atlas are absent public demonstrations do not create a buyer-accessible product.",
        "Commercial availability also says nothing by itself about autonomous capability. Some platforms arrive as programmable machines with SDK access. Some are primarily teleoperated. Some perform scripted social interactions. The 1X NEO early-access program explicitly combines basic autonomy with scheduled remote expert assistance for tasks the robot cannot yet complete. Buyers should treat control mode, safety envelope and support model as core specifications, not footnotes."
      ]
    },
    {
      "heading": "1. Unitree R1 a complete biped with a cited $4,900 starting price",
      "paragraphs": [
        "Unitree lists the R1 from $4,900 through its official store. The robot stands about 123 centimeters tall, weighs roughly 29 kilograms and is offered with 20 to 26 degrees of freedom depending on configuration. Unitree describes open joint and sensor interfaces, simulation support and a multimodal speech-and-vision model, positioning the R1 as an entry platform for developers rather than a sealed consumer appliance.",
        "The attractive base price needs careful reading. Compute, hands, development access and end-effectors can differ across versions. A team buying the R1 for locomotion research may need a different configuration from a team collecting manipulation data. The practical purchase step is to obtain a written configuration sheet showing exactly which actuators, sensors, compute module, battery and software permissions are included."
      ]
    },
    {
      "heading": "2. Unitree G1 the established small humanoid development platform",
      "paragraphs": [
        "The Unitree G1 is listed at $13,500 in the manufacturer’s online store. At about 132 centimeters tall and roughly 35 kilograms, it is compact enough for laboratories that cannot safely manage a 70-kilogram full-size machine. The platform is sold in several configurations, with degrees of freedom, hands and onboard computing changing between the standard and EDU-oriented versions.",
        "G1 has become visible in reinforcement-learning, teleoperation and manipulation work because the hardware can be purchased rather than accessed only through a partnership. Buyers should still separate mobility demonstrations from delivered software. A robot capable of dynamic movement is not automatically supplied with autonomous task policies, a production safety case or reliable grasping for unstructured objects."
      ]
    },
    {
      "heading": "3. Unitree H2 a full-size humanoid with a public $29,900 price",
      "paragraphs": [
        "Unitree lists the 180-centimeter H2 at $29,900. The official specification describes 31 degrees of freedom and joint torque reaching 360 newton-meters. The store also states that only the EDU version supports secondary development, so a buyer intending to run custom controllers or embodied-AI policies should not assume the base listing provides the required access.",
        "H2 is a much larger operational commitment than R1 or G1. Its mass, torque and reach increase the consequences of a fall or control error. A serious purchase plan needs a protected test area, lifting and transport procedures, spare-part availability, emergency-stop architecture and staff who understand high-power legged systems."
      ]
    },
    {
      "heading": "4. Unitree H1 the earlier full-size platform still offered through sales",
      "paragraphs": [
        "The Unitree store continues to list H1 at $90,000 while instructing buyers to contact the company for the real price. H1 stands around 180 centimeters, weighs about 47 kilograms and uses a replaceable 864-watt-hour battery. Unitree publishes a 3.3-meter-per-second running figure for the platform, although delivered control packages and operating limits depend on the purchased configuration.",
        "H1 remains relevant for teams that need a mature Unitree full-size research base and existing developer resources. It is less compelling as an impulse purchase. The quoted package must identify whether arms, hands, perception sensors, high-level control software, training and support are included."
      ]
    },
    {
      "heading": "5. Booster K1 a 95-centimeter humanoid starting at $5,999",
      "paragraphs": [
        "Booster's K1 product page currently offers Buy Now paths for Geek, Education and Professional configurations. Booster's launch release priced the K1 Geek at $5,999. The current product page lists 95 cm height, 19.5 kg weight, 22 DoF and compute options of 48, 117 or 200 TOPS, so buyers should confirm the current configuration price before ordering.",
        "K1 is closely associated with RoboCup and embodied-AI education. That matters because repeated falls, rapid repairs and software access are normal requirements in robot soccer and locomotion research. Its size reduces handling risk compared with a full-size humanoid, but it still requires a clear fall zone and disciplined battery and actuator maintenance."
      ]
    },
    {
      "heading": "6. Booster T1 a developer biped sold in basic, standard and custom versions",
      "paragraphs": [
        "Booster T1 is available through the manufacturer’s Buy Now flow. It is approximately 1.18 meters tall and 30 kilograms, with configurations ranging from 23 degrees of freedom to 41 when seven-degree-of-freedom arms and dexterous hands are added. Booster lists up to 200 TOPS of AI compute and approximately two hours of walking time from the 10.5-amp-hour battery.",
        "The version choice changes the robot’s role. A basic T1 can support locomotion and robot-soccer development. A manipulation program needs the appropriate arms, end-effectors, wrist sensing and cameras. Buyers should compare the complete configured quotation rather than treating the word T1 as one fixed bill of materials."
      ]
    },
    {
      "heading": "7. Booster T2 the larger flagship platform with three professional configurations",
      "paragraphs": [
        "Booster T2 is presented with active Buy Now links for Professional P1, P2 and P3 configurations. The robot stands about 1.4 meters and weighs roughly 42 to 43 kilograms. All three versions use 31 body degrees of freedom, while end-effectors progress from optional hardware to a gripper and then a six-degree-of-freedom dexterous hand.",
        "Booster specifies a maximum dual-arm payload of 10 kilograms, with payload dependent on posture, a walking speed of 3 meters per second and two hours of continuous walking. These are platform specifications and do not establish autonomous handling success across arbitrary tasks."
      ]
    },
    {
      "heading": "8. 1X NEO the clearest consumer early-access offer",
      "paragraphs": [
        "1X accepts a refundable $200 deposit for NEO. The Early Access ownership option is advertised at $20,000 with a three-year warranty, premium support and priority delivery. A $499-per-month subscription is also shown, with the subscription shipment scheduled later. The company states that US deliveries begin in 2026 before expansion to other markets from 2027.",
        "NEO is the most consumer-oriented purchase on this list, but its operating model must be understood before ordering. 1X says early units arrive with basic autonomy and improve over time. For complex chores the robot does not know, customers can schedule Expert Mode, in which a 1X operator remotely supervises actions. That is a hybrid service, not unlimited independent household autonomy."
      ]
    },
    {
      "heading": "9. Reachy Mini a desktop humanoid sold from €350",
      "paragraphs": [
        "Pollen Robotics currently lists Reachy Mini Lite at €350 and Reachy Mini Wireless at €435. Both are desktop kits built around expressive head motion, cameras, microphones and a speaker; the Wireless version adds onboard compute, wireless connectivity, an IMU and battery power.",
        "Reachy Mini does not walk and cannot perform household manipulation. Its value is accessible human-robot interaction, expressive motion, audio-visual applications and AI experimentation on a desk. Pollen’s store states a lead time of up to 90 days and calculates shipping, duties and tax during checkout."
      ]
    },
    {
      "heading": "10. Reachy 2 an open-source bimanual mobile humanoid sold by quotation",
      "paragraphs": [
        "Reachy 2 is available through Pollen Robotics’ Get your Reachy sales channel. The system can be ordered with one or two seven-degree-of-freedom arms and with or without an omniwheel mobile base. Each arm is rated to lift objects up to 3 kilograms. The software stack supports Python, ROS 2 Humble and a VR teleoperation application.",
        "This is a humanoid mobile manipulator rather than a biped. That design removes walking and fall recovery from the problem and concentrates the platform on reaching, seeing and grasping. Research groups studying imitation learning or vision-language-action policies may gain more usable manipulation time from this architecture than from a legged robot that spends much of the project on locomotion."
      ]
    },
    {
      "heading": "11. Ameca a full-size social humanoid available through Engineered Arts",
      "paragraphs": [
        "Engineered Arts offers Ameca through a commercial quotation. The full-size robot is about 1.87 meters tall, weighs roughly 62 kilograms and provides 61 actuated degrees of freedom, including 27 in the head. Its camera, microphone, speech and facial systems are designed for public demonstrations, research and customer-facing installations.",
        "Ameca is designed for expressive interaction and stationary social deployment. The current platform does not provide a walking locomotion system. Buyers should evaluate the Tritium software environment, integration requirements and support options alongside the robot hardware."
      ]
    },
    {
      "heading": "12. Ameca Desktop Gen 3 the facial system without the full-size body",
      "paragraphs": [
        "Engineered Arts marks Ameca Desktop as available and provides both Buy Now and Get a Quote routes. The Gen 3 desktop unit is approximately 525 millimeters tall and has 32 degrees of freedom, 27 of them in the head. It brings Ameca’s facial motion and conversational embodiment into a format that fits on a counter or laboratory bench.",
        "The desktop model is easier to transport and deploy than the full robot, but it is not a mobile worker. Its practical applications are AI-agent embodiment, reception, museums, exhibitions, remote presence and human-robot interaction studies where gaze and facial timing matter more than locomotion."
      ]
    },
    {
      "heading": "13. Ami Desktop Gen 3 a distinct Engineered Arts social character",
      "paragraphs": [
        "Ami Desktop is listed as available now with Buy Now and quotation options. Engineered Arts records a June 22, 2026 release date and a 32-degree-of-freedom desktop architecture. Ami gives customers a different face and character identity while retaining the company’s browser-based management and conversational-robot software environment.",
        "The purchase decision between Ami, Azi and Ameca Desktop is therefore less about locomotion hardware and more about presentation, audience, voice, role design and brand fit. Organizations should evaluate the robot under the lighting, viewing distance, language model and network conditions of the actual installation."
      ]
    },
    {
      "heading": "14. Azi Desktop Gen 3 another currently orderable desktop humanoid",
      "paragraphs": [
        "Azi Desktop is also marked available now and carries Buy Now and Get a Quote controls on the official product page. The package includes the Gen 3 humanoid desktop robot, power equipment and quick-start documentation, with a standard one-year warranty listed in the specifications.",
        "Azi is a social interface rather than a general-purpose manipulator. Its value depends on the quality of the connected AI, dialogue design, animation and operational support. A convincing demo can be built quickly, but a reliable public installation still needs moderation, privacy rules, fallback behavior and staff who can recover the system when speech or network services fail."
      ]
    },
    {
      "heading": "15. Mesmer a custom humanlike robot available to rent or purchase by quote",
      "paragraphs": [
        "Engineered Arts offers Mesmer through Rent Mesmer and Get a Quote channels. Mesmer is built around realistic facial appearance, detailed skin and tightly controlled facial mechanics. Customers use the platform for entertainment, exhibitions, character work and installations where a specific humanlike visual identity is required.",
        "Mesmer should not be confused with a walking industrial humanoid. It is a custom social and performance robot whose commercial value lies in appearance and interaction. Customization, artistic work, installation and content production can be as important to the total project cost as the robot hardware."
      ]
    },
    {
      "heading": "16. NAO6 the proven 58-centimeter education and research humanoid",
      "paragraphs": [
        "NAO6 is sold through quotation and partner channels. Aldebaran's published NAO6 specifications list 58 cm height, about 5.6 kg weight and 25 degrees of freedom, with cameras, microphones, tactile sensing, sonar and an inertial unit.",
        "NAO6 is aimed at interaction, education, rehabilitation studies and algorithm development rather than heavy manipulation. Aldebaran documentation says battery duration varies with use; current support material gives a range from about 45 minutes to two hours, while its NAO6 press material lists about 90 minutes as a typical figure."
      ]
    },
    {
      "heading": "17. PAL Robotics TALOS a 1.75-meter torque-controlled research humanoid",
      "paragraphs": [
        "PAL Robotics sells TALOS through a request-a-quote process. The robot stands 1.75 meters, weighs about 95 kilograms and has 32 degrees of freedom. PAL specifies a 6-kilogram payload per arm with the arm extended, joint-level torque control and a ROS-based software architecture.",
        "TALOS is built for advanced whole-body control, manipulation and industrial research. It requires institutional infrastructure, experienced operators and a serious safety process. The purchase is closer to acquiring a laboratory system than ordering a consumer device, with configuration, training, maintenance and long-term support forming part of the decision."
      ]
    },
    {
      "heading": "18. PAL Robotics ARI a commercially available wheeled social humanoid",
      "paragraphs": [
        "ARI is offered by PAL Robotics for customer service, human-robot interaction and AI research. The robot combines an expressive head and arms with a mobile base and a touchscreen in the torso. PAL highlights person detection, speech and intent recognition and multimodal interaction, while sales are handled through direct contact with the company.",
        "The wheeled base makes ARI suitable for smooth indoor floors without introducing biped balance. Its useful work is conversation, guidance, information delivery and interaction research. Buyers still need to define privacy, navigation boundaries, microphone use and what happens when the conversational system cannot answer a request."
      ]
    },
    {
      "heading": "19. ROBOTIS OP3 a complete miniature biped with a public $13,764.35 price",
      "paragraphs": [
        "ROBOTIS sells the OP3 directly through its US store for $13,764.35. The fully assembled robot is about 510 millimeters tall, weighs roughly 3.5 kilograms without its skin cover and uses 20 degrees of freedom. It includes an Intel NUC, OpenCR controller, HD camera, nine-axis inertial sensing and support for C++, ROS 2 and the DYNAMIXEL SDK.",
        "OP3 is a compact research and competition platform, not a small domestic worker. Its scale makes it practical for gait development, RoboCup, vision and education while keeping falls manageable. The public price and detailed package list make it one of the easiest institutional purchases to budget accurately."
      ]
    },
    {
      "heading": "20. ROBOTIS THORMANG3 a 1.375-meter research humanoid available through ROBOTIS sales",
      "paragraphs": [
        "THORMANG3 remains in the ROBOTIS DYNAMIXEL System catalogue as a full humanoid research platform. The robot is 137.5 centimeters tall, weighs 42 kilograms and has 29 degrees of freedom. Its published hardware includes two Intel NUC computers, force-torque sensors, an IMU, a camera and optional depth camera and LiDAR, with ROS and C++ development support.",
        "Unlike OP3, THORMANG3 does not show a simple public checkout price. Buyers need to contact ROBOTIS for current quotation, production lead time, regional availability and the exact sensor package. Its architecture is mature and well documented, but the software baseline is older than newer ROS 2-first platforms, so migration work should be evaluated before purchase."
      ]
    },
    {
      "heading": "A purchase price is only the first line of the humanoid budget",
      "paragraphs": [
        "A $4,900 robot can become a much larger project once hands, compute, spare actuators, batteries, protective rigging, shipping and import duties are added. Full-size systems may need a gantry, a fall harness, floor reinforcement, secure charging, transport cases and two people for safe handling. Social robots require microphones, network services, content moderation and on-site recovery procedures.",
        "Software access can be more important than peak torque. Confirm whether the purchase includes low-level joint control, sensor streams, simulation models, calibration tools, source code, firmware updates and permission to run custom policies. Some consumer or basic editions deliberately restrict secondary development. A cheaper closed configuration can be unusable for a research team.",
        "Support should be priced before the robot ships. Ask where repairs occur, which parts the customer can replace, how long actuators and batteries take to arrive and whether remote diagnosis is included. A humanoid that waits three months for one proprietary joint is not an operational platform."
      ]
    },
    {
      "heading": "Choose the robot around the experiment, not the most dramatic demo",
      "paragraphs": [
        "A locomotion group needs robust legs, open controllers and repeatable recovery after falls. A manipulation group may achieve more with Reachy 2’s stable mobile base and two seven-degree-of-freedom arms. An interaction lab can deploy NAO6 or an Engineered Arts desktop robot without managing the energy and risk of a full-size biped. A household early adopter considering NEO is buying into a continuing service and learning program rather than a finished catalogue of autonomous chores.",
        "The strongest purchase is the machine whose limitations match the project. In 2026, buyers can finally compare real products, real quotations and real delivery programs. They still need to ignore the choreography of launch videos and examine the delivered sensors, control access, support contract and tasks the robot can repeat without an engineer standing beside it."
      ]
    }
  ],
  "sourceLinks": [
    {
      "label": "Unitree R1 official product page",
      "url": "https://shop.unitree.com/products/unitree-r1"
    },
    {
      "label": "Unitree G1 official store",
      "url": "https://shop.unitree.com/products/unitree-g1"
    },
    {
      "label": "Unitree H2 official store",
      "url": "https://shop.unitree.com/products/unitree-h2"
    },
    {
      "label": "Unitree H1 official product page",
      "url": "https://www.unitree.com/h1"
    },
    {
      "label": "Booster K1 official product page",
      "url": "https://www.booster.tech/booster-k1/"
    },
    {
      "label": "Booster T1 official product page",
      "url": "https://www.booster.tech/booster-t1/"
    },
    {
      "label": "Booster T2 official product page",
      "url": "https://www.booster.tech/booster-t2/"
    },
    {
      "label": "1X NEO official order page",
      "url": "https://www.1x.tech/order"
    },
    {
      "label": "Reachy Mini official store",
      "url": "https://store.pollen-robotics.com/"
    },
    {
      "label": "Reachy 2 official product page",
      "url": "https://www.pollen-robotics.com/reachy/"
    },
    {
      "label": "Engineered Arts Ameca",
      "url": "https://engineeredarts.com/robots/ameca"
    },
    {
      "label": "Engineered Arts Ameca Desktop",
      "url": "https://engineeredarts.com/robots/ameca-desktop"
    },
    {
      "label": "Engineered Arts Ami Desktop",
      "url": "https://engineeredarts.com/robots/ami-desktop"
    },
    {
      "label": "Engineered Arts Azi Desktop",
      "url": "https://engineeredarts.com/robots/azi-desktop"
    },
    {
      "label": "Engineered Arts Mesmer",
      "url": "https://engineeredarts.com/robots/mesmer"
    },
    {
      "label": "Maxtronics NAO6",
      "url": "https://aldebaran.com/en/nao6/"
    },
    {
      "label": "PAL Robotics TALOS",
      "url": "https://pal-robotics.com/robot/talos/"
    },
    {
      "label": "PAL Robotics ARI",
      "url": "https://pal-robotics.com/robot/ari/"
    },
    {
      "label": "ROBOTIS OP3 official store",
      "url": "https://www.robotis.us/robotis-OP3-platform/"
    },
    {
      "label": "ROBOTIS THORMANG3",
      "url": "https://www.robotis.us/thormang/"
    },
    {
      "label": "Booster K1 launch pricing",
      "url": "https://www.prnewswire.com/news-releases/booster-robotics-unveils-booster-k1-ushering-in-the-era-of-embodied-agents-302599614.html"
    },
    {
      "label": "Aldebaran NAO6 press kit",
      "url": "https://www.aldebaran.com/sites/default/files/press-kit/NAO-press-kit-EN-09.2022.pdf"
    },
    {
      "label": "ROBOTIS THORMANG3 e-Manual",
      "url": "https://emanual.robotis.com/docs/en/platform/thormang3/introduction/"
    }
  ],
  "relatedLinks": [
    {
      "label": "Humanoid Robots companies, models and technology",
      "path": "/humanoid-robots"
    },
    {
      "label": "Humanoid robot type guide",
      "path": "/robot-types/humanoid-robots"
    },
    {
      "label": "Global robotics companies directory",
      "path": "/robotics-companies"
    },
    {
      "label": "Robot components and hardware guide",
      "path": "/robot-components"
    }
  ]
},
  {
  "id": "1x-neo-25-dof-tendon-driven-hands",
  "title": "How 1X Built NEO’s 25-DOF Tendon-Driven Hands",
  "slug": "1x-neo-25-dof-tendon-driven-hands",
  "path": "/robotics-news/1x-neo-25-dof-tendon-driven-hands",
  "category": "Humanoid robot hands",
  "datePublished": "2026-07-10",
  "dateModified": "2026-09-25",
  "readingTime": "7 min read",
  "description": "How 1X designed NEO’s 25-DOF tendon-driven hands, including finger motion, wrist movement, compliance and the limits of current demonstrations.",
  "keywords": [
    "1X NEO",
    "1X Technologies",
    "NEO humanoid robot",
    "1X robot hands",
    "25 DOF robotic hand",
    "tendon-driven robotic hand",
    "humanoid robot hands",
    "dexterous robotic manipulation",
    "tactile sensing",
    "compliant robotics",
    "home humanoid robot",
    "domestic robotics",
    "Physical AI",
    "embodied AI",
    "NVIDIA GR00T"
  ],
  "trendReason": "1X's July 2026 engineering release documents a 25-DoF hand and wrist system with 22 fully actuated finger and palm axes plus three wrist axes, force-controlled backdrivable joints and tactile sensing.",
  "keyFacts": [
    "1X documents 25 total degrees of freedom: 22 fully actuated finger and palm axes plus three wrist axes.",
    "1X says every joint is force-controlled and backdrivable, with tendon transmissions that keep larger actuators away from the fingertips.",
    "The tactile skin measures normal force, contact position and shear, which can be used to detect slip after contact.",
    "1X reports up to 45 N distal finger flexion and 17.75 Nm wrist torque; these are manufacturer measurements.",
    "Independent long-duration household reliability data and a standardized third-party test protocol are not public."
  ],
  "videos": [
    {
      "src": "/images/video%20neo1.mp4",
      "title": "1X NEO tendon-driven hand movement",
      "caption": "@techniahqrobot"
    },
    {
      "src": "/images/neo2.mp4",
      "title": "1X NEO dexterous manipulation demonstration",
      "caption": "@techniahqrobot"
    },
    {
      "src": "/images/neo3.mp4",
      "title": "1X NEO hand speed and finger coordination",
      "caption": "@techniahqrobot"
    }
  ],
  "authorName": "TechniaHQRobot",
  "authorHandle": "@techniahqrobot",
  "sections": [
    {
      "heading": "Hand performance constrains household manipulation",
      "paragraphs": [
        "Walking brings a humanoid within reach of a task. Household work then depends on whether the hand can grasp objects with different shapes, stiffness, surface friction and fragility without dropping or crushing them.",
        "1X's July 2026 engineering release describes NEO's integrated hand and wrist as a 25-DoF tendon system with 22 fully actuated finger and palm axes plus three wrist axes."
      ]
    },
    {
      "heading": "Why humanoid robot hands matter",
      "paragraphs": [
        "Most objects inside a home were designed around the dimensions and movement of a human hand. Door handles, glasses, dishes, clothing, electrical plugs and cleaning tools all assume flexible fingers and continuous force adjustment.",
        "A basic parallel gripper can move boxes or repeat a fixed factory task. It struggles when an object changes position, deforms under pressure or requires several contact points at once.",
        "Household work creates constant variation. A robot may need to hold a plate firmly, touch a glass gently and reposition a piece of clothing without a predefined grasp. That makes hand design one of the main hardware limits for domestic humanoid robots."
      ]
    },
    {
      "heading": "A 25-degree-of-freedom tendon-driven design",
      "paragraphs": [
        "1X says the 25-DoF count includes 22 fully actuated finger and palm axes plus three wrist axes. Tendons route force from actuators through the hand while keeping more motor mass away from the fingertips.",
        "The company reports low-ratio transmissions of roughly 5:1 to 15:1, force control and backdrivability at every joint. Those choices support contact control, but tendon tension, routing friction and wear still require calibration and maintenance.",
        "1X reports peak distal flexion force up to 45 N and wrist torque of 17.75 Nm. These figures should remain attributed to the company until comparable independent test data are available."
      ],
      "videoIndex": 0
    },
    {
      "heading": "Tactile sensing changes how the robot controls contact",
      "paragraphs": [
        "Finger position alone does not tell a robot whether an object is secure. The hand also needs information about pressure, contact location and movement between the object and the fingers.",
        "Tactile sensing can help the controller detect when a grasp is too weak, when an object begins to slip or when additional force could damage it.",
        "This feedback is especially important for transparent, reflective or deformable objects that remain difficult for vision systems. A camera may identify a cup while tactile sensors reveal whether the fingers are actually holding it.",
        "The useful measure will be how consistently the system combines vision, touch and force control during long sequences of household manipulation."
      ]
    },
    {
      "heading": "Built around compliance and human safety",
      "paragraphs": [
        "Industrial robots often rely on rigid structures, physical separation and fixed workspaces. A home robot cannot assume that people will remain outside a safety fence.",
        "NEO uses compliant mechanics to reduce the force created during unexpected contact. This does not remove the need for perception, motion limits and emergency stopping. It gives the hardware another layer of protection when software reacts too slowly or an object moves unexpectedly.",
        "Quiet operation also matters inside a home. A robot that works near people for several hours cannot produce the noise, speed or abrupt movement accepted inside some industrial environments.",
        "The hand therefore has to balance force, speed, durability and safe contact rather than maximize grip strength alone."
      ],
      "videoIndex": 1
    },
    {
      "heading": "The hand as an interface for Physical AI",
      "paragraphs": [
        "1X describes the hand as an API to the physical world. Better manipulation hardware allows an AI system to interact with more objects and collect richer training data.",
        "Each grasp can produce synchronized information from cameras, joint positions, tactile sensors, force estimates and robot actions. Successful attempts show the model which actions worked. Failures show where the grasp, trajectory or force selection broke down.",
        "This data can support end-to-end vision-action models that learn from repeated physical interaction rather than relying only on manually programmed motion.",
        "The hardware does not remove the software problem. A dexterous hand still needs perception, task planning, collision avoidance, recovery behavior and enough training data to generalize beyond the demonstrations used during development."
      ]
    },
    {
      "heading": "Designed for household manipulation",
      "paragraphs": [
        "1X positions NEO as a humanoid platform for residential environments. The target tasks include organizing objects, carrying groceries, cleaning, folding laundry and loading household appliances.",
        "These tasks should be described as intended capabilities or demonstrated research tasks unless 1X has confirmed a specific commercial autonomy level.",
        "A short edited video does not reveal intervention rate, task success rate, reset frequency or the number of attempts required. A clip should not be treated as proof that NEO can already perform unrestricted household work without supervision.",
        "Earlier research involving NEO and NVIDIA robotics models showed the platform performing household manipulation tasks such as dishwasher loading. Coverage should remain precise about whether a specific demonstration was autonomous, teleoperated, scripted or assisted."
      ],
      "videoIndex": 2
    },
    {
      "heading": "How close is the design to a human hand?",
      "paragraphs": [
        "A human hand is commonly described as having roughly 27 degrees of freedom depending on how the wrist and smaller joint movements are counted.",
        "NEO's 25-degree-of-freedom design approaches that mechanical range. Matching the number of joints does not automatically reproduce human dexterity.",
        "Human manipulation also depends on dense biological sensing, soft tissue, adaptive grip strategies and years of learned coordination. A robotic hand must reproduce part of that performance through motors, tendons, sensors and control software.",
        "Useful comparisons come from measured task performance: grasp success, slip recovery, positional accuracy, object damage, maintenance and performance on unfamiliar objects."
      ]
    },
    {
      "heading": "What needs to be proven next",
      "paragraphs": [
        "The next useful demonstrations should show long uncut manipulation sequences with failed grasps and recovery behavior left visible.",
        "Relevant measurements include successful cycles, maintenance requirements, tendon replacement intervals, tactile sensor reliability and performance after extended household use.",
        "The autonomy boundary also needs to remain clear. Viewers should know when a task is controlled by a learned policy, teleoperated by a person, executed through a scripted sequence or completed with human assistance.",
        "If the hand maintains its precision and compliance over millions of cycles, it could remove an important hardware limitation for NEO. The remaining challenge would move toward data quality, model reliability and safe decision-making in homes that never remain perfectly organized."
      ]
    },
    {
      "heading": "What the published specifications establish",
      "paragraphs": [
        "1X documents 25 total degrees of freedom, low-ratio tendon transmissions, force-controlled backdrivable joints, tactile sensing, up to 45 N distal flexion force and 17.75 Nm wrist torque. Those figures come from 1X's engineering release.",
        "The same release reports component and finger-assembly testing across millions of cycles and wrist testing beyond two million cycles under high load. Public independent tests do not yet establish long-duration household reliability, maintenance intervals or a standardized comparison with other dexterous hands."
      ]
    }
  ],
  "sourceLinks": [
    {
      "label": "Original TechniaHQRobot X post",
      "url": "https://x.com/techniahqrobot/status/2075553688093888736"
    },
    {
      "label": "WIRED report on 1X NEO’s hand",
      "url": "https://www.wired.com/story/the-1x-neo-robot-has-freaky-fast-fingers/"
    },
    {
      "label": "1X NEO hands engineering release",
      "url": "https://www.1x.tech/discover/neos-hands"
    },
    {
      "label": "1X official website",
      "url": "https://www.1x.tech/"
    }
  ],
  "relatedSearches": [
    "1X NEO robotic hand",
    "25 DOF tendon driven hand",
    "home humanoid robot manipulation",
    "tactile sensing humanoid hands"
  ],
  "seoTitle": "How 1X Built NEO’s 25-DOF Tendon-Driven Dexterous Hands",
  "subtitle": "The hand uses tendon transmission to keep finger mass low while providing a near-human range of motion for household manipulation.",
  "primaryKeyword": "1X NEO tendon-driven hand",
  "tweetId": "2075553688093888736",
  "tweetUrl": "https://x.com/techniahqrobot/status/2075553688093888736",
  "image": "/images/news/1x-neo-25-dof-tendon-driven-hands.svg",
  "imageAlt": "TechniaHQRobot diagram-style title graphic for the 1X NEO 25-DOF tendon-driven hand",
  "imageWidth": 1200,
  "imageHeight": 630,
  "controlMethod": "Mixed machine-articulated and operator-controlled demonstrations",
  "relatedLinks": [
    {
      "label": "How 1X NEO’s Hands Sense Slip, Force and Contact",
      "path": "/robotics/1x-neo-hand-sensing-force-reliability"
    },
    {
      "label": "Why Humanoid Robot Hardware Is Still So Expensive to Build",
      "path": "/robotics/humanoid-robot-hardware-cost-drivers"
    },
    {
      "label": "Why Robot Vision Fails on Glass, Mirrors and Reflective Objects",
      "path": "/robotics/robot-perception-transparent-reflective-objects"
    }
  ],
  "verificationNotes": [
    "The 25-degree-of-freedom architecture is supported by current reporting.",
    "1X says some published motions are machine-articulated and others are operator-controlled, so each demonstration should be labeled separately."
  ]
},
  {
    id: "lingbot-2-embodied-intelligence-shared-environment",
    title:
      "LingBot 2.0 shows why embodied intelligence is harder than one clean robot demo",
    slug: "lingbot-2-embodied-intelligence-shared-environment",
    path: "/robotics-news/lingbot-2-embodied-intelligence-shared-environment",
    category: "Humanoid robots",
    datePublished: "2026-07-05",
    dateModified: "2026-10-11",
    readingTime: "4 min read",
    description:
      "A source-checked look at the LingBot 2.0 family and the supplied multi-task teaser, separating visible chess, water-handling and sorting sequences from published LingBot-VLA 2.0 model evidence and undisclosed demo conditions.",
    keywords: [
      "LingBot 2.0",
      "embodied intelligence",
      "humanoid robot",
      "robot manipulation",
      "object sorting",
      "physical AI",
      "robotics demo",
      "robot embodiment",
    ],
    trendReason:
      "The strongest part of the LingBot 2.0 teaser is not one isolated task. The useful signal is the mix of embodiments and manipulation scenes inside one shared environment.",
    keyFacts: [
      "The supplied TechniaHQRobot post mentions chess, water handling and object sorting as the visible task mix; the clip itself does not disclose control mode or trial count.",
      "Robbyant’s July 2026 LingBot-VLA 2.0 paper reports pretraining on about 60,000 hours of data, including roughly 50,000 hours of robot trajectories across 20 robot configurations and 10,000 hours of egocentric human video.",
      "LingBot-VLA 2.0 expands its action space beyond dual arms to head, waist, mobile base and dexterous-hand degrees of freedom, but paper benchmarks and release videos do not establish the conditions of this specific teaser.",
      "The core evaluation problem is cross-embodiment control plus repeatability, intervention and recovery under changed scenes.",
    ],
    sections: [
      {
        heading: "The teaser is stronger than a normal task demo",
        paragraphs: [
          "Most robot videos are built around one clean action. A hand picks up an object. A mobile base crosses a room. A humanoid waves at the camera. The LingBot 2.0 teaser is more interesting because the post points to several tasks happening inside the same physical world chess, water handling and object sorting.",
          "That matters because embodied intelligence becomes difficult when the robot has to read the scene, act with its body and recover from small changes. A chess piece, a container of water and a sorting object do not stress the same skill. They require different contact forces, visual attention and timing.",
        ],
      },
      {
        heading: "Different bodies create different failure modes",
        paragraphs: [
          "A robot arm can be good at a tabletop task and still fail when the base moves. A humanoid can look impressive standing upright and still struggle with fine hand placement. A mobile robot can navigate a room and still be useless if the gripper cannot control the object. The teaser becomes useful when the viewer watches the embodiments as a system instead of treating the video as one magic robot moment.",
          "The hard part is not only motion. The hard part is matching body shape, sensors, hand control, task planning and the environment. A robot that plays chess does not automatically handle water. A robot that sorts objects does not automatically understand why a piece moved.",
        ],
      },
      {
        heading: "What the published LingBot-VLA 2.0 work adds",
        paragraphs: [
          "Robbyant’s July 2026 LingBot-VLA 2.0 paper describes a policy pretrained on about 60,000 hours of physical data. The authors report 50,000 hours of real-robot trajectories spanning 20 robot configurations plus 10,000 hours of egocentric human video, with a broader action space covering head, waist, mobile base and dexterous hands.",
          "That publication gives useful model-level context for the LingBot family, including cross-embodiment and long-horizon experiments. It should not be used to fill missing fields in the supplied teaser: the clip still needs its own robot version, control mode, trial count, reset rules and failure record.",
        ],
      },
      {
        heading: "How to evaluate a shared-space sequence",
        paragraphs: [
          "For chess, useful fields include piece-localization error, illegal-move rate and recovery after a moved piece. For water handling, report spill rate, fill level, grasp failures and whether force or tactile sensing is used. For sorting, report object classes, changed positions, success over repeated trials and human interventions.",
          "If several robot embodiments appear in the same release, record whether they share one checkpoint or use embodiment-specific adaptation. Cross-embodiment marketing language is much easier to evaluate when the action representation and per-robot post-training are disclosed.",
        ],
      },
      {
        heading: "What still needs proof",
        paragraphs: [
          "A teaser does not prove autonomy level, repeatability or deployment readiness. The useful next questions are simple was the task teleoperated, scripted or autonomous? How many takes were needed? What sensors were active? What happens when the lighting changes, when the object shifts or when a person interrupts the scene?",
          "Until those details are public, the right reading is measured. LingBot 2.0 is worth watching because the task mix points at real embodied AI problems. The proof will come from longer demonstrations with failures, resets and recovery behavior left visible.",
        ],
      },
    ],
    sourceLinks: [
      {
        label: "Original TechniaHQRobot X post",
        url: "https://x.com/techniahqrobot/status/2073916755827630246",
      },
      {
        label: "Official LingBot-VLA 2.0 repository and model report",
        organization: "Robbyant",
        documentTitle: "LingBot-VLA 2.0 training data and evaluation documentation",
        date: "Accessed October 11, 2026",
        url: "https://github.com/Robbyant/lingbot-vla-v2",
      },
    ],
    tweetUrl: "https://x.com/techniahqrobot/status/2073916755827630246",
    relatedSearches: [
      "LingBot 2.0 robot",
      "embodied intelligence robot demo",
      "humanoid robot manipulation",
      "physical AI robotics",
    ],
  },
  {
    id: "apptronik-apollo-2-robot-park-data-factory",
    title:
      "Apptronik Apollo 2 turns Robot Park into a data factory for humanoid robots",
    slug: "apptronik-apollo-2-robot-park-data-factory",
    path: "/robotics-news/apptronik-apollo-2-robot-park-data-factory",
    category: "Humanoid robot data",
    datePublished: "2026-07-06",
    dateModified: "2026-07-23",
    readingTime: "7 min read",
    description:
      "How Apptronik uses Apollo 2, teleoperation, simulation and customer sites to collect humanoid robot data, plus the limits of its Robot Park evidence.",
    seoDescription:
      "How Apptronik uses Apollo 2, teleoperation, simulation and customer sites to collect humanoid robot data, plus the limits of its Robot Park evidence.",
    keywords: [
      "Apptronik",
      "Apollo 2",
      "Robot Park",
      "humanoid robot data",
      "physical AI",
      "robotics data collection",
      "Google DeepMind",
      "humanoid robots",
    ],
    trendReason:
      "Apptronik's June 30, 2026 release shifts attention from a single humanoid demonstration to the infrastructure used to collect, compare and reuse physical task data across robot configurations and partner sites.",
    keyFacts: [
      "Apptronik describes Robot Park as a nearly 90,000-square-foot Austin facility built for repeated humanoid training and testing.",
      "The company presents Apollo 2 in bipedal and wheeled-base configurations rather than as one fixed body design.",
      "The official workflow combines teleoperation, autonomous task execution and high-fidelity simulation.",
      "Apptronik names Google DeepMind, Mercedes-Benz and GXO among the organizations connected to its training or deployment ecosystem, but does not publish task-level success rates or intervention frequency.",
    ],
    sections: [
      {
        heading: "The robot is visible. The data loop is the story",
        paragraphs: [
          "A humanoid announcement usually gets judged by the body. People look at the face, the hands, the walking motion and the video polish. The Apptronik Apollo 2 post points to a deeper story. Robot Park is presented as the asset that can turn repeated physical work into training data.",
          "That is an important distinction. A humanoid robot needs more than a strong mechanical body. It needs examples of tasks, contact events, failures, recoveries, operator corrections, sensor logs and environment changes. A controlled facility can produce that information with much less noise than a random public demo.",
        ],
      },
      {
        heading: "Why a Robot Park matters for physical AI",
        paragraphs: [
          "Physical AI needs data that comes from action. Text and images are not enough. A humanoid has to learn what happens when a hand slips, when a box shifts, when a floor surface changes or when a human walks through the work area. Those events are expensive to collect because the robot must physically attempt the task.",
          "Robot Park gives Apptronik a place to repeat tasks, compare robot versions and capture failures before a system is placed in a more complex environment. The official release says the pipeline combines human teleoperation, autonomous execution and simulation, allowing one task to generate demonstrations, policy rollouts and synthetic variations. That does not make Apollo 2 proven at every job. It shows that Apptronik is building a controlled loop for collecting evidence and improving software across multiple bodies.",
        ],
      },
      {
        heading: "What to watch next",
        paragraphs: [
          "The next useful signals are not only smoother video. Apptronik has not disclosed the number of Apollo 2 units operating at Robot Park, the volume of collected episodes, per-task success, remote intervention frequency or reliability over a complete shift. Those measurements would show whether the facility is producing transferable operational learning rather than a larger collection of curated demonstrations.",
          "Apollo 2 should be judged by the same standard as every humanoid system. The body is only one layer. The training environment, data quality and recovery loop decide whether the platform improves after repeated physical work.",
        ],
      },
    ],
    sourceLinks: [
      {
        label: "Apptronik Welcome to Robot Park",
        url: "https://apptronik.com/company/press-releases-collection/welcome-to-robot-park-where-apptroniks-apollo-goes-to-work",
      },
      {
        label: "Apptronik Apollo official product page",
        url: "https://apptronik.com/our-work/apollo-1",
      },
      {
        label: "Google DeepMind Gemini Robotics paper",
        url: "https://arxiv.org/abs/2503.20020",
      },
      {
        label: "Original TechniaHQRobot X post",
        url: "https://x.com/techniahqrobot/status/2074172555250786458",
      },
    ],
    tweetUrl: "https://x.com/techniahqrobot/status/2074172555250786458",
    relatedSearches: [
      "Apptronik Apollo 2",
      "Robot Park humanoid robots",
      "humanoid robot data factory",
      "Google DeepMind robotics data",
    ],
  },
  {
    id: "t800-humanoid-demo-safety-question",
    title:
      "The T800 humanoid demo raises the real question what would make people feel safe near this robot?",
    slug: "t800-humanoid-demo-safety-question",
    path: "/robotics-news/t800-humanoid-demo-safety-question",
    category: "Humanoid robot demos",
    datePublished: "2026-07-06",
    dateModified: "2026-09-27",
    readingTime: "6 min read",
    description:
      "A source-checked look at the EngineAI T800, separating high-dynamic demonstrations from published hardware specifications, production claims and the safety evidence needed for operation around people.",
    keywords: [
      "T800 humanoid",
      "humanoid robot demo",
      "robot safety",
      "home robots",
      "physical AI",
      "human robot interaction",
      "robot autonomy",
    ],
    trendReason:
      "The T800 post works because it turns a striking demo into a social question. The robot may look powerful, but people want to know whether they would feel safe near it.",
    keyFacts: [
      "EngineAI identifies T800 as its full-size humanoid platform and published a 450 N·m peak-joint-torque and 14,000 W instantaneous joint peak-power specification at CES 2026.",
      "EngineAI announced in May 2026 that the first batch of T800 robots had rolled off its Shenzhen manufacturing line; this is a company production claim, not independent deployment evidence.",
      "The supplied TechniaHQRobot clip can demonstrate visible motion, but it does not disclose the control mode, retry count, safety controller or failure distribution.",
      "The key deployment question is how speed, force, stopping behavior and recovery are constrained around people.",
    ],
    sections: [
      {
        heading: "A strong demo creates trust and fear at the same time",
        paragraphs: [
          "The post has a simple hook this T800 demo looks strong enough to make the product feel worth buying. That is exactly why the safety question becomes unavoidable. A humanoid robot that looks capable also looks consequential. People do not only ask what it can do. They ask what happens when it fails.",
          "For a humanoid near people, the impressive part of a demo is not enough. The viewer needs to know whether the robot can stop, yield, detect contact, limit force and behave predictably when a person gets too close. Those details matter more than a cinematic clip.",
        ],
      },
      {
        heading: "What a buyer should ask before trusting it",
        paragraphs: [
          "The right questions are practical. Is the sequence teleoperated, scripted or autonomous? What sensing is active? Which controller limits joint torque and speed? What happens after a push, a blocked path, a dropped object or communication loss? Can motion be stopped without entering the robot's path?",
          "EngineAI's published high-dynamic specifications establish that T800 is designed for substantial joint output. They do not establish safe human proximity. A deployment needs stopping behavior, permitted operating zones, contact limits, intervention procedures and evidence from repeated failures as well as successes.",
        ],
      },
      {
        heading: "Production evidence is different from safety evidence",
        paragraphs: [
          "EngineAI announced in May 2026 that the first batch of T800 robots rolled off its Shenzhen manufacturing base and described capacity for scaled delivery. That establishes a company-reported manufacturing milestone, not the reliability or safety of a particular customer application.",
          "For a real deployment, track the exact robot configuration, task, operating hours, interventions, maintenance events and safety incidents. Production volume and dynamic performance should remain separate from application-level acceptance testing.",
        ],
      },
      {
        heading: "The product test is social, not only technical",
        paragraphs: [
          "The post asks whether people would feel safe living next to this humanoid robot. That question is useful because home and neighborhood acceptance will depend on more than specs. People react to posture, speed, noise, size, hand motion and whether the machine appears to understand boundaries.",
          "A humanoid can win attention with one video. It earns trust through boring evidence safe stops, clear controls, visible limitations, documented supervision and repeatable behavior when the environment is messy.",
        ],
      },
    ],
    sourceLinks: [
      {
        label: "EngineAI T800 at CES 2026",
        url: "https://en.engineai.com.cn/about-news-media/41.html",
      },
      {
        label: "EngineAI T800 manufacturing announcement",
        url: "https://www.prnewswire.com/news-releases/engineai-launches-shenzhen-intelligent-manufacturing-base-as-first-batch-of-t800-humanoid-robots-roll-off-the-production-line-to-begin-mass-delivery-302785384.html",
      },
      {
        label: "Original TechniaHQRobot X post",
        url: "https://x.com/techniahqrobot/status/2074147859918639204",
      },
    ],
    tweetUrl: "https://x.com/techniahqrobot/status/2074147859918639204",
    relatedSearches: [
      "T800 humanoid robot demo",
      "humanoid robot safety",
      "would you live near a robot",
      "home humanoid robot",
    ],
  },
  {
    id: "nre-skin-humanoid-protective-reflex",
    title:
      "NRE-Skin gives robots a local protective reflex and injury detection",
    slug: "nre-skin-humanoid-protective-reflex",
    path: "/robotics-news/nre-skin-humanoid-protective-reflex",
    category: "Robot tactile sensing",
    datePublished: "2026-07-06",
    dateModified: "2026-09-26",
    readingTime: "6 min read",
    description:
      "A source-checked explanation of NRE-Skin, the PNAS neuromorphic electronic skin that encodes touch into pulse trains, triggers local protective reflexes and detects damaged skin regions.",
    keywords: [
      "NRE-skin",
      "electronic skin",
      "robot tactile sensing",
      "humanoid robot reflex",
      "robot safety",
      "PNAS",
      "robot pain feedback",
      "tactile skin",
    ],
    trendReason:
      "The topic is strong because it sounds emotional, but the engineering point is concrete tactile skin can detect dangerous contact and trigger protective behavior before damage spreads.",
    keyFacts: [
      "The PNAS paper describes a neuromorphic robotic electronic skin that converts tactile intensity and location into neural-like pulse trains.",
      "When stimulation exceeds a defined pain threshold, the demonstrated control logic can trigger a local motor reflex without waiting for the higher-level processing path.",
      "The skin also detects damaged regions and uses a modular quick-release design so affected sections can be localized and replaced.",
      "The authors demonstrate the system on a humanoid robot, but the biological pain analogy describes an engineered signal and reflex mechanism, not evidence of subjective experience.",
    ],
    sections: [
      {
        heading: "What the PNAS system actually does",
        paragraphs: [
          "NRE-Skin uses a hierarchical neuromorphic architecture that converts tactile intensity and location into pulse trains. The paper describes normal touch sensing, threshold-based pain detection and injury localization as separate functions.",
          "In the humanoid demonstration, an excessive stimulus can trigger a high-priority local motor response. The important engineering idea is a short protective path between tactile detection and motion, not a claim that the robot experiences pain."
        ],
      },
      {
        heading: "Injury sensing and repair are part of the design",
        paragraphs: [
          "The paper also describes active injury detection: the skin can identify where a damaged region is located and continue alerting the system. Its modular construction is designed so damaged sections can be removed and replaced.",
          "That is distinct from ordinary pressure sensing. A deployment-oriented test would need to measure false alarms, detection latency, repair cycles and sensing accuracy after repeated abrasion or partial damage."
        ],
      },
      {
        heading: "What the study does not establish",
        paragraphs: [
          "The paper demonstrates tactile encoding, local reflex behavior and injury detection under its experimental setup. It does not establish long-term field durability, universal collision safety or a certified human-robot safety system.",
          "Terms such as pain and injury are neuromorphic analogies for thresholded sensory and damage signals. They should not be presented as evidence of consciousness or subjective suffering."
        ],
      },
    ],
    sourceLinks: [
      {
        label: "PNAS paper",
        url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12772184/",
      },
      {
        label: "PubMed record",
        url: "https://pubmed.ncbi.nlm.nih.gov/41428887/",
      },
      {
        label: "Original TechniaHQRobot X post",
        url: "https://x.com/techniahqrobot/status/2074103955450143121",
      },
    ],
    tweetUrl: "https://x.com/techniahqrobot/status/2074103955450143121",
    relatedSearches: [
      "NRE-skin robot",
      "robot electronic skin PNAS",
      "humanoid robot tactile skin",
      "pain-like robot feedback",
    ],
  },
  {
  "id": "unitree-humanoid-concert-glitch-human-reaction",
  "title": "Unitree humanoid concert glitch what the video actually shows",
  "headline": "Unitree Humanoid Concert Glitch What the Video Shows",
  "seoTitle": "Unitree Humanoid Concert Glitch What the Video Shows",
  "slug": "unitree-humanoid-concert-glitch-human-reaction",
  "path": "/robotics-news/unitree-humanoid-concert-glitch-human-reaction",
  "category": "Robot demos",
  "datePublished": "2026-07-06",
  "dateModified": "2026-10-11",
  "readingTime": "8 min read",
  "description": "Technical analysis of a Unitree humanoid concert glitch, the performer response, possible failure classes, stage safety and what the clip cannot prove.",
  "subtitle": "The clip shows an apparent motion anomaly; its cause is unknown. A short concert clip can reveal timing and safety questions without proving whether the robot suffered a balance, communication, choreography or hardware failure.",
  "keywords": [
    "Unitree humanoid",
    "Unitree robot concert",
    "humanoid robot glitch",
    "robot demo failure",
    "stage robot safety",
    "human robot interaction",
    "humanoid recovery",
    "robot choreography"
  ],
  "showTableOfContents": true,
  "researchDate": "July 27, 2026",
  "informationVerifiedAsOf": "July 27, 2026",
  "featuredSnippet": "The clip shows a Unitree-branded humanoid deviating from the expected stage sequence while a performer stays calm and interacts with it. The video does not expose telemetry, operator input or controller state, so it cannot identify the technical cause or prove autonomous recovery.",
  "introduction": [
    "A humanoid robot on a live stage has to do several things at once. It must stay balanced, keep time with a choreographed routine, respect the position of nearby performers and remain inside a safe operating envelope under changing lights, sound, vibration and radio traffic. A small timing error can become visible immediately because the audience knows what the movement was supposed to look like.",
    "In the circulated clip, the robot appears to lose the expected sequence or timing. The performer responds calmly, makes friendly contact and turns the interruption into part of the show. That reaction keeps the audience engaged, but it should not be mistaken for a technical diagnosis or proof that the robot recovered autonomously.",
    "The useful analysis begins by separating what the pixels show from what the video leaves hidden."
  ],
  "trendReason": "The clip spread because a machine failure and a human improvisation happened in the same few seconds. For robotics, the more important value is that the scene exposes the gap between a rehearsed motion sequence and a safely managed public performance.",
  "keyFindings": [
    "The robot visibly deviates from the expected stage behavior, but the clip does not reveal the internal fault source.",
    "The performer remains calm and prevents the moment from escalating into panic or an uncontrolled crowd reaction.",
    "Physical contact with a moving humanoid is safe only when the routine, speed limits and intervention procedure have been engineered for it.",
    "A successful public performance demonstrates a bounded choreography, not factory reliability or long-duration unsupervised autonomy.",
    "Telemetry, operator logs, controller state and repeated runs would be needed to classify the event technically."
  ],
  "evidenceClassification": [
    "Directly visible a Unitree-branded humanoid departs from the apparent choreography and a performer responds in close proximity.",
    "Reported in the source post the performer hugged the robot and gave it a high five.",
    "Not visible robot model, control mode, remote-operator input, error code, emergency-stop state, network status and recovery logic.",
    "Inference only the event may involve choreography timing, state-machine transition, localization, balance confidence, communications or hardware. The clip cannot select among these causes."
  ],
  "sections": [
    {
      "id": "what-the-video-confirms",
      "heading": "What the video confirms",
      "paragraphs": [
        "The robot is operating in a public performance beside a human. Its motion no longer matches the apparent sequence, and the performer notices the deviation. The performer stays composed, approaches the robot and reframes the interruption as interaction rather than an emergency.",
        "Those observations are enough to discuss stage procedure and human-robot coordination. They are not enough to claim a motor failure, software crash or autonomous recovery. A video records external motion. It does not expose the control state that produced it."
      ],
      "bullets": [
        "The platform carries Unitree branding, but the exact humanoid model is not confirmed by the clip alone.",
        "The robot remains upright during the visible sequence.",
        "The performer is inside close physical range.",
        "No telemetry overlay, operator view or fault log is shown."
      ]
    },
    {
      "id": "why-live-stages-are-difficult",
      "heading": "Why a concert stage is difficult for a humanoid robot",
      "paragraphs": [
        "A concert is more controlled than a street, but it is far less controlled than a robotics laboratory. Lighting changes rapidly. Reflections and shadows move across the floor. Loud sound can create vibration. Wireless systems share the venue. Performers cross near the robot, and the machine must hit visible timing cues without moving outside its safety boundary.",
        "The robot may also be executing a pre-authored motion sequence rather than perceiving and planning the entire performance online. In that case, synchronization becomes critical. A delayed cue, skipped state or changed performer position can make a mechanically stable robot look confused even when its low-level balance controller is working."
      ],
      "bullets": [
        "Dynamic lighting can affect cameras and visual references.",
        "Stage vibration and floor compliance can alter foot-contact estimates.",
        "Wireless congestion can affect commands or monitoring if the routine depends on radio links.",
        "Human spacing changes from rehearsal to live performance.",
        "The safety plan must account for a fall, stop, missed cue and loss of communication."
      ]
    },
    {
      "id": "possible-failure-classes",
      "heading": "Possible failure classes, without pretending to know the cause",
      "paragraphs": [
        "Several different faults can produce a similar external result. A choreography controller may miss a transition. A command link may arrive late. The robot may enter a protective state after confidence drops. A footstep may complete with an unexpected timing offset. A remote operator may pause the sequence. A joint, battery or thermal warning may limit motion.",
        "None of these explanations can be selected from the clip. The responsible wording is to name the classes of failure and state what evidence would distinguish them."
      ],
      "table": {
        "id": "failure-class-table",
        "title": "What different failure classes would require",
        "columns": [
          "Failure class",
          "Evidence needed",
          "What the clip provides"
        ],
        "rows": [
          [
            "Choreography or state-machine error",
            "Behavior-state log, cue timestamps and sequence trace",
            "Only the visible deviation"
          ],
          [
            "Communication interruption",
            "Packet, latency and Agent or operator-link logs",
            "No network data"
          ],
          [
            "Balance or contact-confidence event",
            "IMU, foot-force, joint and estimator telemetry",
            "Robot remains upright, but internal confidence is unknown"
          ],
          [
            "Hardware or power limit",
            "Joint faults, current, voltage, battery and thermal logs",
            "No diagnostic data"
          ],
          [
            "Human or remote intervention",
            "Operator console recording and command history",
            "Intervention is not shown clearly enough to classify"
          ]
        ],
        "note": "The same visible pause or off-sequence motion can have several unrelated causes."
      }
    },
    {
      "id": "performer-response",
      "heading": "The performer response helped the show, but safety depends on preparation",
      "paragraphs": [
        "The performer does something socially effective they remain calm and keep the audience from treating the robot as an uncontrolled threat. That response reduces confusion and preserves the performance. It also demonstrates why staff behavior belongs inside the deployment plan for robots operating around the public.",
        "The physical contact deserves more caution. Hugging or touching a humanoid can be reasonable in a rehearsed routine with verified low-speed behavior, a known safe state and a trained performer. It should not become general advice for an unexpected robot fault. A person who does not know the robot state should keep distance, preserve an escape path and follow the event operator’s stop procedure."
      ],
      "callout": "A calm human reaction is valuable. Unplanned physical contact is safe only when the robot state and intervention procedure are known."
    },
    {
      "id": "stage-safety-stack",
      "heading": "What a serious stage safety stack should include",
      "paragraphs": [
        "Crowd control, choreography and staff training are part of the robot system. The controller cannot compensate for people entering the fall zone or for a performer improvising inside an unsafe arc. The event design should make a robot failure boring motion slows or stops, the human steps away, staff isolate the machine and the show continues without uncertainty.",
        "The exact controls depend on the platform and venue, but the safety case should be explicit rather than assumed from a successful rehearsal."
      ],
      "bullets": [
        "Defined robot operating zone and fall envelope",
        "Speed and joint-torque limits appropriate for human proximity",
        "Accessible emergency stops and a named person authorized to use them",
        "Loss-of-link and low-confidence behavior that moves to a safe state",
        "Rehearsed performer response for pause, drift, fall and unexpected contact",
        "Floor, lighting and radio checks before the live sequence",
        "A recovery route that does not require staff to stand in front of an active robot"
      ]
    },
    {
      "id": "what-the-demo-proves",
      "heading": "What the performance proves and what it does not",
      "paragraphs": [
        "The performance shows that a Unitree humanoid can execute a prepared public routine closely enough to share a stage with people for at least part of the sequence. It also shows that the event team accepted the remaining risk and had a human capable of improvising when the behavior deviated.",
        "It does not establish unsupervised autonomy, long-duration uptime, industrial cycle reliability or safe operation around untrained members of the public. Stage success and factory success use different evidence. A concert values timing, appearance and audience experience. A factory values repeatability, intervention rate, safe recovery, maintenance and output across thousands of cycles."
      ]
    },
    {
      "id": "evidence-needed",
      "heading": "Evidence needed for a technical diagnosis",
      "paragraphs": [
        "A credible post-event analysis would combine the video with controller and operator records. Engineers would align every data stream to the same clock, identify the expected state, locate the first deviation and determine whether the robot, operator or safety system initiated the stop or recovery.",
        "Without that record, the correct conclusion is limited a visible stage anomaly occurred, the human response kept the moment socially controlled, and the clip leaves the technical cause unresolved."
      ],
      "bullets": [
        "Robot model and firmware version",
        "Planned choreography and cue timestamps",
        "Joint position, velocity, current and fault status",
        "IMU, foot-contact and balance-estimator confidence",
        "Battery voltage, temperature and power limits",
        "Network and remote-control logs",
        "Emergency-stop and protective-stop state",
        "Number of rehearsals, failed attempts and successful repeated runs"
      ]
    }
  ],
  "limitations": [
    "The article analyzes a short social-media clip rather than raw multi-camera footage.",
    "The exact Unitree model is not confirmed from an official event record available with the clip.",
    "No telemetry, error code, operator log or network trace is available.",
    "The video angle may hide staff, safety equipment or remote-control actions.",
    "The performer’s physical contact cannot be evaluated fully without the rehearsal and safety procedure.",
    "The sequence does not provide evidence about long-duration reliability or deployment outside entertainment."
  ],
  "conclusion": [
    "The most defensible reading is simple. A Unitree humanoid lost the expected stage behavior. A performer stayed calm and turned the interruption into a socially acceptable moment. That is useful evidence about public interaction and event preparation.",
    "The clip is not a fault report. Calling it a balance failure, communications failure or autonomous recovery would add information the video does not contain. Better robotics coverage keeps the visible event, the engineering possibilities and the missing evidence separate."
  ],
  "faqs": [
    {
      "question": "Which Unitree humanoid is shown in the concert clip?",
      "answer": "The clip shows Unitree branding, but this article does not identify the exact model because an official event record or clear model-specific view is not available in the supplied evidence."
    },
    {
      "question": "Did the humanoid lose balance?",
      "answer": "The robot deviates from the apparent routine, but it remains upright in the visible sequence. Internal balance confidence and foot-contact telemetry are not shown."
    },
    {
      "question": "Did the robot recover autonomously?",
      "answer": "The clip does not reveal whether recovery came from onboard control, a remote operator, a preprogrammed transition or a restart cue."
    },
    {
      "question": "Was the performer response safe?",
      "answer": "The calm response helped manage the audience. The safety of close contact depends on the robot state, speed limits, rehearsal and intervention procedure, none of which are visible in full."
    },
    {
      "question": "What should happen when a stage robot glitches?",
      "answer": "The robot should enter a defined safe state, performers should move outside the operating and fall zones, and trained staff should use the established stop and recovery procedure."
    },
    {
      "question": "Does this clip prove humanoid robots are unreliable?",
      "answer": "No. It proves that one visible anomaly occurred during one public sequence. Reliability requires repeated-run data, intervention rates, fault logs and operating conditions."
    }
  ],
  "sourceLinks": [
    {
      "label": "Original TechniaHQRobot X post",
      "documentTitle": "Original TechniaHQRobot video post",
      "organization": "TechniaHQRobot",
      "date": "2026-07-06",
      "url": "https://x.com/techniahqrobot/status/2074084637135994904"
    },
    {
      "label": "Unitree H1 official product page",
      "documentTitle": "Unitree H1 humanoid robot",
      "organization": "Unitree Robotics",
      "date": "Accessed 2026-07-27",
      "url": "https://www.unitree.com/h1"
    },
    {
      "label": "Unitree G1 official product page",
      "documentTitle": "Unitree G1 humanoid agent",
      "organization": "Unitree Robotics",
      "date": "Accessed 2026-07-27",
      "url": "https://www.unitree.com/g1"
    },
    {
      "label": "ISO 13482 overview",
      "documentTitle": "ISO 13482 Robots and robotic devices safety requirements for personal care robots",
      "organization": "International Organization for Standardization",
      "date": "Official standard page",
      "url": "https://www.iso.org/standard/53820.html"
    }
  ],
  "tweetUrl": "https://x.com/techniahqrobot/status/2074084637135994904",
  "relatedSearches": [
    "Unitree humanoid concert glitch",
    "Unitree robot stage performance",
    "humanoid robot safety around performers",
    "robot choreography failure",
    "human response to robot fault"
  ],
  "relatedLinks": [
    {
      "label": "Humanoid robots technical guide",
      "path": "/robot-types/humanoid-robots"
    },
    {
      "label": "Robot sensors guide",
      "path": "/robot-sensors"
    },
    {
      "label": "Unitree wheeled robot dog analysis",
      "path": "/robotics/unitree-wheeled-robot-dog-bionic-wheelchair"
    },
    {
      "label": "T800 humanoid demo safety question",
      "path": "/robotics-news/t800-humanoid-demo-safety-question"
    }
  ]
},
  {
    id: "humanoid-robot-internal-hardware-breakdown",
    title: "Inside a Humanoid Robot: Actuators, Cabling, Bearings and Thermal Design",
    slug: "humanoid-robot-internal-hardware-breakdown",
    path: "/robotics-news/humanoid-robot-internal-hardware-breakdown",
    category: "Robot hardware",
    datePublished: "2026-07-05",
    readingTime: "5 min read",
    description:
      "TechniaHQRobot breaks down why humanoid robots are dense mechanical systems made of machining, bearings, electronics, sensors, cabling and thermal constraints before software even takes control.",
    keywords: [
      "humanoid robot hardware",
      "robot actuators",
      "CNC machining",
      "robot bearings",
      "robot sensors",
      "thermal design",
      "robot cables",
      "humanoid engineering",
    ],
    trendReason:
      "Humanoid reliability depends on packaging, actuator design, wiring, thermal paths and manufacturing processes that are mostly invisible in a finished robot.",
    keyFacts: [
      "Figure says Figure 02's forearm was its top hardware failure point at BMW and redesigned Figure 03 wrist electronics to remove a distribution board and dynamic wrist cabling.",
      "1X says its Hayward NEO factory manufactures critical components including motors, batteries, structures, transmissions and sensors in-house.",
      "Mechanical packaging, cable motion, heat and manufacturability can limit robot uptime even when the control software remains unchanged.",
    ],
    sections: [
      {
        heading: "The outside hides the hardest work",
        paragraphs: [
          "A humanoid robot looks simple when the shell is closed. The viewer sees a torso, legs, arms and hands. Inside the robot, every joint is a crowded stack of structure, bearings, actuator components, sensors, cables, fasteners and thermal paths.",
          "A useful hardware review asks how the robot is assembled, cooled, wired and serviced. Figure's BMW retrospective gives one concrete example: the Figure 02 forearm was its top hardware failure point, and Figure 03 removed a wrist distribution board and dynamic cabling to reduce complexity and improve thermal management.",
        ],
      },
      {
        heading: "CNC precision and bearings decide motion quality",
        paragraphs: [
          "Precision machining gives the robot's parts their geometry. Bearings support rotation and loads. If the machined surfaces are poor, the joint can bind, flex, heat or wear. If the bearing selection is wrong, the robot may lose stiffness, repeatability or lifetime.",
          "Those failures do not look dramatic in a press video. They appear later as noise, backlash, drift, broken cables, hot motors and maintenance cost. A walking robot depends on small tolerances repeated across many joints.",
        ],
      },
      {
        heading: "Electronics and heat are not secondary details",
        paragraphs: [
          "Motors, motor drivers, batteries, processors and sensors all produce heat. The robot also needs cables that survive bending through hips, knees, shoulders, elbows, wrists and neck movement. A cable that rubs inside the body can become a failure point long before the AI model makes a mistake.",
          "Production design also changes the hardware stack. Figure says Figure 03 moved away from extensive CNC-from-billet construction toward more cast, stamped and injection-molded parts, while 1X describes vertically integrated production of motors, batteries, structures, transmissions and sensors for NEO. Those choices affect assembly time, serviceability and scale."

        ],
      },
    ],
    sourceLinks: [
      {
        label: "Figure 02 BMW hardware reliability retrospective",
        url: "https://www.figure.ai/news/production-at-bmw",
      },
      {
        label: "Figure 03 manufacturing design",
        url: "https://www.figure.ai/news/introducing-figure-03",
      },
      {
        label: "1X NEO Factory",
        url: "https://www.1x.tech/discover/neo-factory",
      },
      {
        label: "Original TechniaHQRobot X post",
        url: "https://x.com/techniahqrobot/status/2073714993749487741",
      },
    ],
    tweetUrl: "https://x.com/techniahqrobot/status/2073714993749487741",
    relatedSearches: [
      "humanoid robot hardware",
      "robot actuator components",
      "robot bearing design",
      "humanoid robot manufacturing",
    ],
  },
  {
    id: "home-robot-watering-plants-zen-future",
    title: "What a Plant-Watering Robot Clip Can and Cannot Show",
    slug: "home-robot-watering-plants-zen-future",
    path: "/robotics-news/home-robot-watering-plants-zen-future",
    category: "Domestic robots",
    datePublished: "2026-07-05",
    readingTime: "4 min read",
    description:
      "A plant-watering robot clip illustrates a bounded household task. This review separates the visible sequence from unverified autonomy, sensing, spill handling and recovery claims.",
    keywords: [
      "home robot",
      "domestic robot",
      "robot watering plants",
      "humanoid chores",
      "service robot",
      "robot assistant",
      "physical AI home",
    ],
    trendReason:
      "The clip is useful as a narrow robotics example because watering requires positioning a container near a target without spilling, while the video alone does not reveal the robot's control mode or retry history.",
    keyFacts: [
      "The supplied post describes a robot in a purple apron watering plants by a koi pond.",
      "The article treats the clip as a domestic task example, not a confirmed consumer deployment.",
      "The robotics lesson is that simple chores still require perception, navigation and manipulation.",
    ],
    sections: [
      {
        heading: "The boring chore is the serious benchmark",
        paragraphs: [
          "Watering a plant is a bounded manipulation task: the robot must approach the target, position the container, control its orientation and stop the pour without hitting the plant or surrounding objects.",
          "The supplied clip does not disclose the robot model, control mode, spill rate, water-volume sensing, failed attempts or human intervention. Those fields are needed before treating the sequence as evidence of autonomous household operation.",
        ],
      },
      {
        heading: "Homes punish fragile robots",
        paragraphs: [
          "Household reliability depends on variables the clip does not test: moved furniture, different plant heights, partial occlusion, pets, floor changes and recovery after a failed grasp or spill.",
          "A useful evaluation would repeat the same watering task across changed pot positions and approach angles, then report task success, interventions, spills, collisions and recovery behavior.",
        ],
      },
      {
        heading: "What evidence would support household use",
        paragraphs: [
          "For this task, useful measurements include successful pours per trial, water delivered to the target, spill volume, collision count, human interventions and recovery after the container or plant position changes.",
          "Until those measurements are available, the clip should be described as a prepared plant-watering sequence rather than proof of general home autonomy.",
        ],
      },
    ],
    sourceLinks: [
      {
        label: "Original TechniaHQRobot X post",
        url: "https://x.com/techniahqrobot/status/2073822785860776287",
      },
    ],
    tweetUrl: "https://x.com/techniahqrobot/status/2073822785860776287",
    relatedSearches: [
      "home robot chores",
      "robot watering plants",
      "domestic humanoid robot",
      "service robot home",
    ],
  },
  {
    id: "pain-like-feedback-loop-humanoid-robots",
    title:
      "Pain-like feedback could make humanoids more durable, not more human",
    slug: "pain-like-feedback-loop-humanoid-robots",
    path: "/robotics-news/pain-like-feedback-loop-humanoid-robots",
    category: "Robot safety",
    datePublished: "2026-07-05",
    dateModified: "2026-10-11",
    readingTime: "5 min read",
    description:
      "A technical guide to pain-like protective feedback in robots, separating tactile danger detection, local reflexes and hardware damage sensing from claims about biological pain or consciousness.",
    keywords: [
      "pain-like feedback",
      "humanoid robots",
      "robot tactile sensing",
      "robot durability",
      "robot reflex",
      "physical AI",
      "robot safety",
      "electronic skin",
    ],
    trendReason:
      "Protective contact feedback can help a robot detect a damaging load and trigger a response while its higher-level controller handles the task.",
    keyFacts: [
      "A protective feedback loop can detect excessive contact and trigger a local stop, retraction or force reduction before a slower high-level planner responds.",
      "The 2025 PNAS NRE-Skin work demonstrates one implementation using neuromorphic tactile pulse encoding, threshold-based local reflexes and active injury detection.",
      "Protective reflexes and tactile sensing can reduce hardware risk, but they do not by themselves establish safe human interaction or certified collision performance.",
      "The word pain is an engineering analogy in these systems; public evidence does not establish subjective experience.",
    ],
    sections: [
      {
        heading: "A protective loop has three separate jobs",
        paragraphs: [
          "A useful protective architecture separates contact sensing, danger classification and response. Sensors first measure force, pressure, deformation or damage indicators. A threshold or learned detector then classifies whether the contact is normal, risky or damaging. The response can reduce force, retract a limb, stop a joint or escalate to the main controller.",
          "Keeping those stages explicit matters because each can fail independently. A tactile sensor may saturate, a threshold may be poorly calibrated, or a reflex may move the robot into another obstacle."
        ],
      },
      {
        heading: "NRE-Skin is one concrete research example",
        paragraphs: [
          "The PNAS NRE-Skin paper provides a concrete example rather than a metaphor alone. Its electronic skin encodes tactile stimuli into neural-like pulse trains, detects stimuli above a pain threshold, triggers a local protective motor response and can localize damaged skin regions.",
          "Those results are research evidence under the authors’ test conditions. They should not be generalized into a claim that all humanoids now have pain-like sensing or that the mechanism guarantees safe operation around people."
        ],
      },
      {
        heading: "What deployment evidence should measure",
        paragraphs: [
          "A deployment test should report detection latency, contact-force range, false positives, missed hazardous contacts, reflex response time, post-contact recovery and whether repeated impacts damage or recalibrate the sensor.",
          "Human-safety claims need additional application-level evidence such as stopping distance, impact energy, fault response and risk assessment. A protective tactile reflex is one layer in that architecture, not a substitute for it."
        ],
      },
    ],
    sourceLinks: [
      {
        label: "PNAS NRE-Skin paper",
        url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12772184/",
      },
      {
        label: "Original TechniaHQRobot X post",
        url: "https://x.com/techniahqrobot/status/2073785647270096978",
      },
    ],
    tweetUrl: "https://x.com/techniahqrobot/status/2073785647270096978",
    relatedSearches: [
      "pain-like feedback robot",
      "humanoid robot reflex",
      "robot tactile feedback",
      "electronic skin humanoid",
    ],
  },
  {
    id: "robots-design-around-the-world-40-systems",
    title:
      "Robot Design Around the World: How Form Follows the Job",
    slug: "robots-design-around-the-world-40-systems",
    path: "/robotics-news/robots-design-around-the-world-40-systems",
    category: "Robot design",
    datePublished: "2026-07-03",
    dateModified: "2026-09-27",
    readingTime: "6 min read",
    description:
      "An engineering guide to robot form factors using selected systems from a 40-design collection, with concrete criteria for reach, payload, mobility, safety, tooling and maintenance.",
    keywords: [
      "robot design",
      "industrial robot arms",
      "ABB robots",
      "KUKA robots",
      "humanoid robots",
      "robot form factor",
      "robotics around the world",
      "factory automation",
    ],
    trendReason:
      "The post works because it avoids treating robotics as one category. A welding arm, a cobot, a humanoid and a mobile platform are shaped by different work.",
    keyFacts: [
      "The supplied post introduces a look at 40 robot designs from industrial arms to humanoids.",
      "ABB's current articulated-robot portfolio spans small, collaborative and large industrial arms with task-specific payload, reach and mounting options rather than one general-purpose body.",
      "KUKA's KR CYBERTECH family is a six-axis industrial platform offered across multiple payload and reach variants for handling, assembly, welding and related cell-based work.",
      "The article uses the post as a design map, not a ranking of the best robots.",
    ],
    sections: [
      {
        heading: "Robot design begins with the job",
        paragraphs: [
          "A robot arm built for welding does not need to look like a humanoid. A warehouse mobile robot does not need a face. A surgical robot, drone, cobot and factory arm solve different physical problems. Their shapes are not cosmetic choices. They are engineering answers to task, environment and cost.",
          "That is why a global design map is useful. It shows how wide the field already is. Robotics is not waiting for humanoids to become useful. Industrial arms, AMRs, inspection robots and specialized service systems have been doing physical work with forms that fit their jobs.",
        ],
      },
      {
        heading: "Industrial arms show mature design discipline",
        paragraphs: [
          "The post names ABB's IRB Series and KUKA's KR CYBERTECH Series as industrial arm examples. These systems are designed around repeatability, reach, payload, duty cycle, safety integration and factory uptime. Their value comes from doing the same motion accurately for long periods.",
          "That is a different design culture from humanoids. A humanoid has to move through spaces made for people. An industrial arm can be installed inside a controlled cell. The constraints are different, so the bodies are different.",
        ],
      },
      {
        heading: "Humanoids add flexibility but also cost",
        paragraphs: [
          "Humanoids attract attention because their body plan suggests generality. Legs, arms and hands make sense in human spaces. The cost is complexity. Balance, manipulation, batteries, perception, safety and heat all become harder when the robot has to carry itself through the world.",
          "A strong robot design map should not crown one form factor. The useful comparison is task fit: required reach, payload, mobility, tool access, installation space, safety boundary and maintenance burden.",
        ],
      },
      {
        heading: "Current industrial families show why form factor matters",
        paragraphs: [
          "ABB's current catalog ranges from small IRB arms to larger industrial families and AMRs. The IRB 1200 Next Generation, for example, is optimized for high-speed, high-precision tasks with variants up to 9 kg payload, while larger ABB families cover much heavier loads.",
          "KUKA's KR CYBERTECH family uses the same principle of task-specific variants. Current models span multiple payload and reach combinations and support handling, assembly, machining and welding workflows. These are mature examples of designing the body around a constrained process instead of asking one robot morphology to do everything.",
        ],
      },
      {
        heading: "A practical design checklist",
        paragraphs: [
          "Start with the work envelope. Record the horizontal and vertical reach needed at every tool pose, then add clearance for fixtures, guards and cable routing. A six-axis arm can cover a large fixed volume, while a mobile base trades mounting rigidity for access to several stations. A humanoid adds stairs, doorways and human-height work surfaces, but every moving joint adds mass, wiring, control effort and service points.",
          "Next compare payload at reach, rather than payload as one headline number. A gripper, camera, force sensor and tool all consume payload before the workpiece is lifted. For mobile robots, include floor transitions, turning radius, slope limits and localization conditions. For legged systems, include balance margin and the effect of carrying a load away from the torso.",
          "Record how the robot fails and how a technician recovers it. Check emergency-stop architecture, guarding or speed-and-separation controls, tool-change access, calibration steps, replacement time for wear parts and whether a failed joint blocks the full cell. These details help determine whether a body shape fits a production task.",
        ],
      },
    ],
    sourceLinks: [
      {
        label: "ABB robotics portfolio",
        url: "https://www.abb.com/global/en/areas/robotics/products/robots",
      },
      {
        label: "ABB IRB 1200 Next Generation",
        url: "https://www.abb.com/global/en/areas/robotics/products/robots/articulated-robots/small-robots/irb-1200-next-generation",
      },
      {
        label: "KUKA KR CYBERTECH",
        url: "https://www.kuka.com/en-gb/products/robotics-system/industrial-robots/kr-cybertech",
      },
      {
        label: "Original TechniaHQRobot X post",
        url: "https://x.com/techniahqrobot/status/2073025273725874605",
      },
    ],
    tweetUrl: "https://x.com/techniahqrobot/status/2073025273725874605",
    relatedSearches: [
      "robot designs around the world",
      "industrial robot arms",
      "ABB IRB Series",
      "KUKA KR CYBERTECH",
      "humanoid robot design",
    ],
  },
  {
    id: "documented-humanoid-deployments-vs-demo-visibility-map",
    title:
      "Humanoid robot maps need to separate documented pilots from demo visibility",
    slug: "documented-humanoid-deployments-vs-demo-visibility-map",
    path: "/robotics-news/documented-humanoid-deployments-vs-demo-visibility-map",
    category: "Humanoid deployment analysis",
    datePublished: "2026-07-04",
    dateModified: "2026-09-26",
    readingTime: "5 min read",
    description:
      "How to separate humanoid factory deployments, commercial pilots, customer trials and public demonstrations, using current Figure and Agility records as documented examples.",
    keywords: [
      "humanoid robot map",
      "humanoid robot deployments",
      "Figure 03 BMW",
      "Agility Digit GXO",
      "humanoid robot pilots",
      "robot deployment evidence",
      "demo visibility",
    ],
    trendReason:
      "Deployment maps are only useful when each entry states the robot generation, site, task, date, evidence source and whether the record is a pilot, commercial deployment or public demonstration.",
    keyFacts: [
      "Figure reports that Figure 02 ran more than 1,250 hours at BMW Plant Spartanburg, loaded more than 90,000 parts and contributed to more than 30,000 X3 vehicles.",
      "Figure 03 returned to BMW Plant Spartanburg in 2026 for a logistics sequencing workflow, which is a separate robot generation and task from the Figure 02 record.",
      "Agility reports more than 65,000 operating hours for Digit 4 across customer sites and a 100,000-tote milestone at GXO at about 98% on-task accuracy.",
    ],
    sections: [
      {
        heading: "A map can mislead if it treats every video as deployment",
        paragraphs: [
          "A pin on a map can make a staged demonstration look equivalent to a customer deployment. Each entry should state the robot generation, named site, task, date, control boundary and source before it is counted as deployment evidence.",
          "A factory pilot, a customer trial, a research pilot and a conference demo do not mean the same thing. They have different stakes, different supervision levels and different evidence quality. A good map should show those layers instead of flattening them into one headline.",
        ],
      },
      {
        heading: "Documented pilots are not the same as scale",
        paragraphs: [
          "A pilot can be real while remaining narrow. One site, one task and substantial engineering support can produce useful operating evidence without establishing fleet-wide maturity.",
          "Current public records show why generation labels matter. Figure 02 has a published BMW production record, while Figure 03 later returned for a different sequencing workflow. Agility's long-running operating metrics refer to Digit 4, while Digit 5 was announced in September 2026 for later availability. A map should keep those records separate.",
        ],
      },
      {
        heading: "What a better humanoid map should include",
        paragraphs: [
          "Useful fields include robot generation, company, country, named site, task, deployment status, source date, autonomy boundary, unit count when confirmed, operating hours, intervention rules and whether the metric is company-reported or independently documented.",
          "When a source omits unit count, intervention rate or failure distribution, the map should leave that field undisclosed rather than infer it from a video or announcement.",
        ],
      },
    ],
    sourceLinks: [
      {
        label: "Figure 02 production record at BMW",
        url: "https://www.figure.ai/news/production-at-bmw",
      },
      {
        label: "Figure 03 at BMW logistics workflow",
        url: "https://www.figure.ai/news/f-03-at-bmw",
      },
      {
        label: "Digit 5 launch and Digit 4 operating record",
        url: "https://www.agilityrobotics.com/content/agility-unveils-digit-5-humanoid-robot-built-for-cooperatively-safe-work-at-scale",
      },
      {
        label: "GXO commercial Digit deployment",
        url: "https://www.agilityrobotics.com/content/gxo-signs-industry-first-multi-year-agreement-with-agility-robotics",
      },
    ],
    tweetUrl: "https://x.com/techniahqrobot/status/2073420739806364108",
    relatedSearches: [
      "humanoid robot deployment map",
      "Figure 03 BMW factory",
      "Agility Digit GXO deployment",
      "humanoid robot pilots",
      "humanoid robot commercial deployment",
    ],
  },
  {
    id: "22-countries-global-robotics",
    title: "Robotics Across 22 Countries: Industry, Research and Robot Platforms",
    slug: "22-countries-global-robotics",
    path: "/robotics-news/22-countries-global-robotics",
    description:
      "In-depth analysis of the countries shaping global robotics through industrial robots, humanoids, drones, medical robotics, agriculture, defense, logistics, components and Physical AI.",
    category: "Global robotics analysis",
    datePublished: "2026-06-29",
    keywords: [
      "global robotics",
      "robotics countries",
      "industrial robots",
      "humanoid robots",
      "drones",
      "medical robotics",
      "agricultural robots",
      "defense robotics",
      "warehouse robots",
      "physical AI",
      "robot actuators",
      "robot sensors",
    ],
    relatedSearches: [
      "Which countries lead global robotics?",
      "Leading countries in industrial robots",
      "Humanoid robotics by country",
      "Global robotics industry healthcare defense",
    ],
  },
  {
    id: "build-first-robot-limited-budget",
    title: "How to Build Your First Robot from Scratch on a Limited Budget",
    slug: "build-first-robot-limited-budget",
    path: "/robotics-news/build-first-robot-limited-budget",
    description:
      "A practical guide to choosing a realistic first robot and comparing Arduino, ESP32, Raspberry Pi, Jetson, motors, drivers, sensors, batteries, software, safety, budget and the practical limits of a low-cost robotics project.",
    category: "DIY robotics",
    datePublished: "2026-06-29",
    keywords: [
      "DIY robotics",
      "first robot",
      "Arduino robot",
      "ESP32 robot",
      "Raspberry Pi robot",
      "ROS 2 robot",
      "DC motors",
      "motor driver",
      "TB6612FNG",
      "L298N",
      "robot sensors",
      "robotics budget",
    ],
    relatedSearches: [
      "How do you build your first robot?",
      "Arduino or ESP32 for a robot?",
      "Beginner mobile robot budget",
      "Beginner ROS 2 robot",
    ],
  },
  {
    id: "physical-ai-explainer",
    title: "Physical AI is becoming the language of modern robotics",
    slug: "physical-ai-explainer",
    description:
      "A clear explanation of why physical AI, AI robotics, humanoid robot systems, robot sensors, robot components, robot software, robot machine learning examples, and embodied control matter for robots that act in physical environments.",
    category: "AI robotics",
    datePublished: "2026-05-24",
    keywords: [
      "physical AI",
      "AI robotics",
      "AI powered robots",
      "robotics AI",
      "robotics",
      "robot machine learning examples",
      "edge artificial intelligence",
      "robot sensors",
      "robot components",
      "robot software",
    ],
    relatedSearches: [
      "How Does a Robot Work?",
      "Robot machine learning examples",
      "LiDAR technology for robots",
      "Humanoids vs Robotics",
    ],
  },
  {
    id: "warehouse-amr-trend",
    title: "Warehouse robots are moving from pilots to daily operations",
    slug: "warehouse-amr-trend",
    description:
      "AMRs, AGVs, picking robots, sorting systems, cobots, industrial robots, mobile robot platforms, automation, logistics robots, and factory workflows show where robot automation creates visible operational value.",
    category: "Warehouse robotics",
    datePublished: "2026-05-24",
    keywords: [
      "warehouse robots",
      "AMR robots",
      "robots in logistics",
      "industrial robotics",
      "industrial robot",
      "cobot",
      "collaborative robots",
      "collaborative robot",
      "automation",
      "factory",
      "robotic arm",
      "mobile robot",
      "#industry40",
      "#cobot",
    ],
    relatedSearches: [
      "Industrial Robotics Definition",
      "What is a cobot?",
      "Robotics trends in France",
      "France 2030 robotics",
    ],
  },
  {
    id: "humanoid-robot-factory-use",
    title: "Humanoid robots are being tested for human designed workspaces",
    slug: "humanoid-robot-factory-use",
    description:
      "Humanoid robot searches, Unitree Go1, Unitree B1, Boston Dynamics Spot, Atlas, Tesla Bot, Pepper, Nao, ASIMO and Aibo show how public attention moves between demos, brands and real deployment limits.",
    category: "Humanoid robots",
    datePublished: "2026-05-24",
    keywords: [
      "humanoid robots",
      "robots in factories",
      "future of robotics",
      "humanoid robot",
      "humanoid robot",
      "humanoid robot",
      "Unitree",
      "Unitree Go1",
      "Unitree B1",
      "Unitree Laikago",
      "Boston Dynamics Spot",
      "Spot Boston Dynamics",
      "Atlas Boston Dynamics",
      "robot Atlas",
      "Tesla Bot",
      "Pepper SoftBank",
      "Nao SoftBank",
      "ASIMO Honda",
      "Aibo Sony",
      "#robotics",
      "#humanoidrobot",
      "#AI",
    ],
    relatedSearches: [
      "Humanoid robot presentations",
      "Unitree Go1 Robot Price",
      "Order a Pepper robot in France",
      "Humanoids vs Robotics",
    ],
  },
  {
    id: "medical-robotics-impact",
    title: "Medical robots show the human side of robotics progress",
    slug: "medical-robotics-impact",
    description:
      "Surgical robots, rehabilitation robots, prosthetic robotics, DaVinci, CMR Surgical, telemedicine, medical exoskeletons, hospital delivery robots and surgical robot searches show how robotics supports healthcare work.",
    category: "Medical robotics",
    datePublished: "2026-05-24",
    keywords: [
      "medical robots",
      "surgical robots",
      "robots in healthcare",
      "surgical robot",
      "rehabilitation robot",
      "rehabilitation robots",
      "exoskeleton",
      "medical exoskeleton",
      "DaVinci",
      "CMR Surgical",
      "telemedicine",
      "#roboticsurgery",
      "#medtech",
    ],
    relatedSearches: [
      "surgical robot",
      "exoskeleton",
      "rehabilitation robot",
      "medical exoskeleton",
    ],
  },
  {
    id: "domestic-robots-search-demand",
    title: "Domestic robots still dominate search volume",
    slug: "domestic-robots-search-demand",
    description:
      "Robot vacuum searches remain strong because buyers compare Roomba iRobot, Braava, Ecovacs Deebot, Xiaomi robot vacuums, Dyson, robot lawn mowers, kitchen robots, pool-cleaning robots and stand mixers before choosing a product.",
    category: "Domestic robotics",
    datePublished: "2026-06-02",
    keywords: [
      "robot vacuum",
      "robot vacuum cleaner",
      "home robot",
      "robot lawn mower",
      "kitchen robot",
      "pool-cleaning robot",
      "cooking robot",
      "multifunction kitchen robot",
      "stand mixer reviews",
      "iRobot",
      "Roomba iRobot",
      "Braava",
      "Ecovacs Deebot",
      "Xiaomi robot vacuum",
      "Dyson",
      "#robotvacuum",
      "#homerobotics",
    ],
    relatedSearches: [
      "Best robot vacuum 2026",
      "Robot vacuum price",
      "Multifunction kitchen robot",
      "Stand mixer reviews",
    ],
  },
  {
    id: "education-diy-robotics-searches",
    title: "Education and DIY robotics turn search traffic into builders",
    slug: "education-diy-robotics-searches",
    description:
      "Educational robots, teaching robots, Lego Mindstorms, Arduino Robot, mBot, Scratch, Python, robotics clubs, RoboCup and online robotics courses show the learning path behind hands-on robotics education.",
    category: "Education robotics",
    datePublished: "2026-06-02",
    keywords: [
      "educational robot",
      "teaching robot",
      "Lego Mindstorms",
      "Arduino robot",
      "Arduino robot",
      "Arduino Robot",
      "mBot",
      "Scratch",
      "Python",
      "robotics club",
      "Robocup",
      "educational kits",
      "robot programming",
      "#educationalrobot",
      "#STEM",
    ],
    relatedSearches: [
      "How Do You Program an Arduino Robot?",
      "Buy an Educational Robot for a Child",
      "Free online robotics course",
      "robot programming",
    ],
  },
  {
    id: "drones-iot-automation-searches",
    title:
      "Drones and IoT automation sit between consumer interest and field robotics",
    slug: "drones-iot-automation-searches",
    description:
      "Drone, surveillance robot, construction robot, construction-site drone, latest delivery robot, underwater fishing robot and #automation searches connect public curiosity to inspection, delivery, marine robotics and IoT automation.",
    category: "Drones and automation",
    datePublished: "2026-06-02",
    keywords: [
      "drone",
      "drones",
      "surveillance robots",
      "construction robot",
      "construction-site drones",
      "latest delivery robot model",
      "Underwater Fishing Robot for Ocean Use",
      "IoT",
      "#automation",
      "#industrialrobotics",
    ],
    relatedSearches: [
      "Latest delivery robot model",
      "Underwater Fishing Robot for Ocean Use",
      "drone",
      "construction-site drones",
    ],
  },
];

const legacyInboundLinks = {
  "best-ai-robot-vacuums-2026": [
    { label: "Cleaning robot management software", path: "/articles/cleaning-robot-management-software-guide" },
    { label: "Waymo and Tesla perception stacks", path: "/robotics-news/waymo-vs-tesla-robotaxi" },
    { label: "Visual SLAM and LiDAR SLAM", path: "/robotics-software/visual-slam-vs-lidar-slam" },
    { label: "Autonomous delivery robots", path: "/robotics/autonomous-delivery-robots-compared" },
  ],
  "robotics-engineer-salary-2026": [
    { label: "Robotics training data services", path: "/articles/robotics-training-data-services" },
    { label: "Robotics component manufacturers", path: "/articles/robotics-component-manufacturers-suppliers" },
    { label: "Robotics programming languages", path: "/robotics-software/robotics-programming-languages" },
    { label: "Robotics courses and certifications", path: "/robotics/best-robotics-courses-certifications" },
    { label: "Industrial robot brands compared", path: "/robotics/fanuc-vs-abb-vs-kuka-vs-yaskawa" },
  ],
  "how-to-become-robotics-engineer": [
    { label: "NVIDIA Isaac Sim installation guide", path: "/articles/nvidia-isaac-sim-download-installation" },
    { label: "Robotics courses for engineers", path: "/robotics/best-robotics-courses-certifications" },
    { label: "Programming languages used in robotics", path: "/robotics-software/robotics-programming-languages" },
    { label: "Visual and LiDAR SLAM", path: "/robotics-software/visual-slam-vs-lidar-slam" },
  ],
  "ros-2-tutorial-for-beginners": [
    { label: "Robot-agnostic fleet management", path: "/articles/robot-agnostic-fleet-management-software" },
    { label: "Motor sensing for warehouse robots", path: "/articles/motor-sensing-warehouse-robotics" },
    { label: "Visual SLAM vs LiDAR SLAM", path: "/robotics-software/visual-slam-vs-lidar-slam" },
    { label: "Autonomous delivery robot navigation", path: "/robotics/autonomous-delivery-robots-compared" },
    { label: "Robotics programming language guide", path: "/robotics-software/robotics-programming-languages" },
  ],
  "best-robotics-kits-for-beginners": [
    { label: "Stereo cameras for robotics", path: "/articles/stereo-cameras-for-robotics" },
    { label: "Robotic arm linkage bearings", path: "/articles/robotic-arm-linkage-bearings" },
    { label: "Best robotics courses in 2026", path: "/robotics/best-robotics-courses-certifications" },
    { label: "Robotics programming languages", path: "/robotics-software/robotics-programming-languages" },
    { label: "SLAM sensors and software", path: "/robotics-software/visual-slam-vs-lidar-slam" },
  ],
  "robot-dog-prices-2026": [
    { label: "Autonomous robot companies", path: "/articles/autonomous-robot-companies-2026" },
    { label: "Transport robots", path: "/articles/transport-robots" },
    { label: "Best exoskeleton robots", path: "/robotics/best-exoskeleton-robots-2026" },
    { label: "Robotic arm cost in 2026", path: "/robotics/robotic-arm-cost-2026" },
    { label: "Warehouse automation cost", path: "/robotics/warehouse-automation-cost-roi" },
  ],
  "robot-lawn-mower-technology": [
    { label: "Robot-agnostic fleet software", path: "/articles/robot-agnostic-fleet-management-software" },
    { label: "Waymo vs Tesla Robotaxi", path: "/robotics-news/waymo-vs-tesla-robotaxi" },
    { label: "Autonomous delivery robots compared", path: "/robotics/autonomous-delivery-robots-compared" },
    { label: "Warehouse AMR cost and ROI", path: "/robotics/warehouse-automation-cost-roi" },
  ],
  "amr-vs-agv": [
    { label: "AMR robot companies", path: "/articles/amr-robot-companies-warehouse" },
    { label: "Warehouse robotic picking systems", path: "/articles/warehouse-robotic-picking-systems" },
    { label: "Transport robot guide", path: "/articles/transport-robots" },
    { label: "Warehouse automation ROI", path: "/robotics/warehouse-automation-cost-roi" },
    { label: "Autonomous delivery robots", path: "/robotics/autonomous-delivery-robots-compared" },
    { label: "Visual SLAM vs LiDAR SLAM", path: "/robotics-software/visual-slam-vs-lidar-slam" },
  ],
  "cobots-vs-industrial-robots": [
    { label: "Industrial robot manufacturers and suppliers", path: "/articles/industrial-robot-manufacturers-suppliers-2026" },
    { label: "Robotic production line guide", path: "/articles/robotic-production-line" },
    { label: "Robotic arm cell cost", path: "/robotics/robotic-arm-cost-2026" },
    { label: "FANUC vs ABB vs KUKA vs Yaskawa", path: "/robotics/fanuc-vs-abb-vs-kuka-vs-yaskawa" },
    { label: "Warehouse robot ROI", path: "/robotics/warehouse-automation-cost-roi" },
  ],
  "ai-robot-pets-2026": [
    { label: "Entertainment robots market and companies", path: "/articles/entertainment-robots-market-companies" },
    { label: "Assistive robotics companies", path: "/articles/assistive-robotics-companies" },
    { label: "Humanoid robot operating hours", path: "/articles/humanoid-robot-operating-hours" },
    { label: "Exoskeleton control systems", path: "/articles/exoskeleton-control-systems" },
    { label: "Medical and industrial exoskeletons", path: "/robotics/best-exoskeleton-robots-2026" },
    { label: "Da Vinci surgical robot", path: "/robotics/da-vinci-surgical-robot-guide" },
    { label: "Waymo vs Tesla Robotaxi", path: "/robotics-news/waymo-vs-tesla-robotaxi" },
  ],
};

const repeatedEvidenceBoilerplate = "The classification records what the source establishes and leaves unstated fields as not publicly disclosed. It should not be extended to different robot versions, sites or tasks without new evidence.";

function removeRepeatedEvidenceBoilerplate(value) {
  if (typeof value === "string") {
    // Code examples depend on indentation and blank lines. Leave unrelated strings intact.
    return value.includes(repeatedEvidenceBoilerplate)
      ? value.replace(repeatedEvidenceBoilerplate, "").trim()
      : value;
  }
  if (Array.isArray(value)) return value.map(removeRepeatedEvidenceBoilerplate);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, removeRepeatedEvidenceBoilerplate(item)]));
  }
  return value;
}

const newsArticleEntries = [
  ...perceptronMk15Article20260924,
  ...chinaTrends20260924,
  ...chinaPhysicalAIData20260924,
  ...chinaRobotics20260924,
  ...currentRoboticsNews20260914,
  ...researchNotes202609,
  ...humanoidOperationsArticles2026,
  ...robotResearchFrontiers2026,
  ...trendingPhysicalAIArticles2026,
  ...searchConsoleArticles2026,
  ...seoArticlesBatch2_2026,
  ...seoArticles2026,
  ...physicalAIClusterLibrary2026,
  ...editorialLibrary2026,
  ...physicalAIArticles2026,
  ...featureArticles2026,
  ...legacyNewsArticleEntries,
];

export const newsArticles = newsArticleEntries.map(removeRepeatedEvidenceBoilerplate).map((article) => ({
  ...article,
  relatedLinks: [
    ...(article.relatedLinks || []),
    ...(legacyInboundLinks[article.slug] || []),
  ].filter((item, index, items) => items.findIndex((candidate) => candidate.path === item.path) === index),
  excerpt: article.excerpt || article.subtitle || article.description,
  tags: article.tags || article.keywords?.slice(0, 5) || [],
  sources: article.sources || article.sourceLinks || [],
})).map(applyEditorialMedia);
